import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { BANDWIDTH, GraphType, RESPONSE_TIME, RESPONSES_CODES, TRAFIC } from '../../components/highcharts/graph/graph';
import { SpringPage } from '../../page';
import { buildHttpParams } from '../../utils/request-utils';
import { prependIfMissing } from '../../utils/strings';
import _ from 'lodash';
import { delay, Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AbstractOrganizationService } from '../abstractOrganizationService';
import { DateRange } from '../../utils/date-range';
import { ADMIN_CONTEXT, BaseLogRequest, ContextEnum } from '../../models/ContextSiteModel';
import { CONFIG, Config } from '../../../../config/config';

export enum ActionEnum {
  AUTHORIZED = 'authorized',
  SUSPICIOUS = 'suspicious',
  BLOCKED = 'blocked',
  REPLIED = 'replied',
}

export const Actions = Object.values(ActionEnum);

export const Causes = [
  'analyzedOK',
  'passthroughRule',
  'whitelistedIp',
  'blacklistedIp',
  'blacklistedIpCluster',
  'urlException',
  'analyzedKO',
  'geoBlocked',
  'blockedByRule',
  'banned',
  'cdnHit',
  'cdnBlocked',
  'challengeEmitted',
  'challengeSucceeded',
  'challengeFailed',
  'challenge',
  'acmeChallenge',
] as const;

export type Cause = (typeof Causes)[number];

export enum LogFiltersType {
  IN = 'in',
  NOTIN = 'notIn',
  FLAT = 'FLAT',
  LTE = 'lte',
  GTE = 'gte',
}

export type LogRequest = BaseLogRequest;

export interface AttackCategory {
  uid: string;
  name: string;
  hidden: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LogsService extends AbstractOrganizationService {
  private cancelPendingRequests$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    readonly auth: AuthService,
    private readonly router: Router,
    @Inject(CONFIG) private readonly config: Config,
  ) {
    super(auth);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.cancelPendingRequests$.next();
      }
    });
  }

  getLogs(request: LogRequest, pageParams: PageParams, context: ContextEnum): Observable<SpringPage<LogEntry>> {
    let page: Observable<SpringPage<any>>;
    if (!this.config.mockDashboardData) {
      page = this.http
        .get<SpringPage<any>>(this.getLogsEndpoint('', context), {
          params: buildHttpParams({ ...pageParams, ...request }),
        })
        .pipe(takeUntil(this.cancelPendingRequests$));
    } else {
      console.log('mockData getLogs', request, pageParams);
      page = this.getLogsMockData().pipe(delay(this.config.mockDataDelay));
    }
    return page.pipe(
      map((logs) => ({
        totalElements: logs.totalElements,
        content: logs.content.map((log) => this.parseLog(log)),
      })),
    );
  }

  // BEGIN-NOSCAN
  private getLogsMockData() {
    return of({
      content: [
        {
          cause: 'analyzedOK',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/articles/9290518/les-nouveaux-piliers-de-la-collaboration-en-entreprise.html',
            method: 'GET',
          },
          clientIP: '114.119.136.64',
          clientPort: '47021',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          dateMilli: 1798030112193,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            referer:
              'https://lescomptesdelacrypt.com/articles/9290518/les-nouveaux-piliers-de-la-collaboration-en-entreprise.html',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-encoding': 'gzip, deflate',
            'accept-language': 'en',
            connection: 'Close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 14:35:12 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 15.374894,
          contentLength: 5164,
          url: 'https://lescomptesdelacrypt.com/articles/9290518/les-nouveaux-piliers-de-la-collaboration-en-entreprise.html',
          responseCode: 200,
        },
        {
          action: 'suspicious',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/_profiler/phpinfo',
            method: 'GET',
          },
          clientIP: '45.148.10.124',
          clientPort: '12000',
          site: 'samuelbenais.fr',
          dateMilli: 1795329982132,
          cache: 'NONE',
          drives: [
            {
              param: 'path-info',
              driveUid: '3DCFC614CA4342A18D160D023A1BA9A0',
              type: 'i',
              belief: 50000,
              label: {
                label: 'PHP - PHPInfo',
                description: "pénaliser les tentatives d'accès au phpinfo",
                category: 'Accès à un fichier sensible',
              },
            },
            {
              param: 'request-uri',
              driveUid: '3DCFC614CA4342A18D160D023A1BA9A0',
              type: 'i',
              belief: 50000,
              label: {
                label: 'PHP - PHPInfo',
                description: "pénaliser les tentatives d'accès au phpinfo",
                category: 'Accès à un fichier sensible',
              },
            },
            {
              param: 'user-agent',
              driveUid: 'F028520F83CB40BD8EED8D20845B64AC',
              type: 'h',
              belief: 10000,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Navigateur ancien',
                description: 'pénalise les anciennes versions de navigateurs',
                category: 'User-Agent suspect',
              },
            },
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 299,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 22480,
          rt: 20000,
          mainAttackLabel: 'Accès à un fichier sensible',
          mainAttack: {
            id: 18,
            uid: '8c600638-1409-4504-b0c3-9a22a9b2a283',
            name: 'Accès à un fichier sensible',
            hidden: false,
          },
          requestHeaders: {
            host: 'samuelbenais.fr',
            'user-agent':
              'Mozilla/5.0 (iPod; U; CPU iPhone OS 2_2_1 like Mac OS X; en-us) AppleWebKit/525.18.1 (KHTML, like Gecko) Version/3.1.1 Mobile/5H11a Safari/525.20',
            'accept-charset': 'utf-8',
            'accept-encoding': 'gzip, br',
            'cdn-loop': 'cloudflare; loops=1',
            'cf-connecting-ip': '45.148.10.124',
            'cf-ipcountry': 'NL',
            'cf-ray': '9d37ea5c1a2d2fda-AMS',
            'cf-visitor': '{"scheme":"https"}',
          },
          responseHeaders: {
            server: 'Netlify',
            'content-type': 'text/html; charset=utf-8',
            'content-length': '1203',
            age: '42171',
            'cache-control': 'public,max-age=0,must-revalidate',
            'cache-status': '"Netlify Edge"; hit',
            'content-encoding': 'br',
            date: 'Wed, 25 Feb 2026 14:33:02 GMT',
            etag: '1564750152-ssl-df',
            'strict-transport-security': 'max-age=31536000',
            vary: 'Accept-Encoding',
            'x-nf-request-id': '01KJAKFDJAZS429Z2JCN96048N',
          },
          auditMode: true,
          countryCode: 'NL',
          responseTimeMs: 155.156803,
          contentLength: 1203,
          url: 'https://samuelbenais.fr/_profiler/phpinfo',
          responseCode: 404,
          botUA: false,
        },
        {
          cause: 'urlException',
          action: 'authorized',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 17,
            'request-uri': '/url-exception.html',
            method: 'POST',
          },
          clientIP: '8.0.7.0',
          clientPort: '50032',
          site: 'tests-non-reg.protectyour.store',
          dateMilli: 1792753570694,
          cache: 'NONE',
          requestHeaders: {
            'content-type': 'application/x-www-form-urlencoded',
            host: 'tests-non-reg.protectyour.store',
            referer: 'https://tests-non-reg.protectyour.store',
            'content-length': '17',
            'user-agent':
              'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/111.0.0.0 Safari/537.36',
            accept: '*/*',
          },
          responseHeaders: {
            server: 'nginx',
            'content-type': 'text/html; charset=UTF-8',
            'cache-control': 'private',
            date: 'Tue, 24 Feb 2026 17:19:30 GMT',
            vary: 'Accept-Encoding',
          },
          countryCode: 'US',
          responseTimeMs: 4.242174,
          contentLength: 1546,
          url: 'https://tests-non-reg.protectyour.store/url-exception.html',
          responseCode: 200,
          botUA: false,
        },
        {
          cause: 'passthroughRule',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 17,
            'request-uri': '/exception.html',
            method: 'POST',
          },
          clientIP: '8.0.7.1',
          ruleId: '2577',
          clientPort: '50096',
          action: 'authorized',
          site: 'tests-non-reg.protectyour.store',
          dateMilli: 1790173571025,
          cache: 'NONE',
          requestHeaders: {
            'content-type': 'application/x-www-form-urlencoded',
            host: 'tests-non-reg.protectyour.store',
            referer: 'https://tests-non-reg.protectyour.store',
            'content-length': '17',
            'user-agent':
              'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/111.0.0.0 Safari/537.36',
            accept: '*/*',
          },
          responseHeaders: {
            server: 'nginx',
            'content-type': 'text/html; charset=UTF-8',
            'cache-control': 'private',
            date: 'Tue, 24 Feb 2026 17:19:31 GMT',
            vary: 'Accept-Encoding',
          },
          countryCode: 'US',
          responseTimeMs: 13.01822,
          contentLength: 1543,
          url: 'https://tests-non-reg.protectyour.store/exception.html',
          responseCode: 200,
          rulePriority: 3,
          botUA: false,
        },
        {
          cause: 'whitelistedIp',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/v2/organizations/unit1896/tls-options/unit1896-ea4776e5-b299-4186-98b8-8c71e4024acf',
            method: 'DELETE',
          },
          clientIP: '152.228.196.172',
          clientPort: '52788',
          action: 'authorized',
          site: 'api-stg.ogosecurity.com',
          dateMilli: 1787585265453,
          cache: 'NONE',
          rt: 0,
          requestHeaders: {
            'content-type': 'application/json; charset=UTF-8',
            host: 'api-stg.ogosecurity.com',
            'user-agent': 'Go-http-client/2.0',
            'accept-encoding': 'gzip',
            'x-ogo-auth': '_OGO_DELETED_',
          },
          responseHeaders: {
            'access-control-allow-credentials': 'true',
            'access-control-allow-headers':
              'Origin, Accept, X-Requested-With, Content-Type, Access-Control-Request-Method, Access-Control-Request-Headers, Cookie, lang',
            'access-control-allow-methods': 'POST, GET, HEAD, OPTIONS',
            'access-control-allow-origin': 'https://d1-stg.ogosecurity.com',
            'access-control-expose-headers': 'Content-Disposition, Set-Cookie',
            'cache-control': 'no-cache, no-store, max-age=0, must-revalidate',
            date: 'Wed, 25 Feb 2026 13:14:25 GMT',
            'strict-transport-security': 'max-age=31536000',
            'x-content-type-options': 'nosniff',
            'x-frame-options': 'Deny',
            'x-xss-protection': '1; mode=block',
          },
          auditMode: false,
          countryCode: 'FR',
          responseTimeMs: 218.680023,
          contentLength: 0,
          url: 'https://api-stg.ogosecurity.com/v2/organizations/unit1896/tls-options/unit1896-ea4776e5-b299-4186-98b8-8c71e4024acf',
          responseCode: 204,
          botUA: false,
        },
        {
          cause: 'cdnHit',
          clientIP: '5.223.73.226',
          cache: 'HIT_CDN',
          site: 'www.ogosecurity.com',
          action: 'authorized',
          botUA: true,
          dateMilli: 1784983968000,
          responseHeaders: {
            'content-length': '0',
            'content-type': 'text/html; charset=UTF-8',
          },
          requestInfo: {
            method: 'GET',
            scheme: 'http',
            'content-size': 597,
            protocol: 'HTTP/1.1',
            'request-uri': '/favicon.ico',
          },
          requestHeaders: {
            'user-agent':
              'Better Stack Better Uptime Bot Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36',
            'x-forwarded-proto': 'https',
            'x-forwarded-for': '5.223.73.226',
            host: 'www.ogosecurity.com',
          },
          countryCode: 'SG',
          responseTimeMs: {
            source: '0.0',
            parsedValue: 0,
          },
          contentLength: 526,
          url: 'http://www.ogosecurity.com/favicon.ico',
          responseCode: 302,
        },
        {
          cause: 'analyzedKO',
          action: 'blocked',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/articles/9665880/attention-required--cloudflare.html',
            method: 'GET',
          },
          clientIP: '47.128.55.216',
          clientPort: '64004',
          site: 'lescomptesdelacrypt.com',
          dateMilli: 1782383749629,
          cache: 'NONE',
          botUA: true,
          drives: [
            {
              param: 'user-agent',
              driveUid: 'E5B0316FE406445585FB89F28874ECED',
              type: 'h',
              belief: 100000,
              label: {
                label: 'User-Agent de scan',
                description: 'pénaliser les requêtes présentant un User-Agent de scan (ex: nmap)',
                category: 'User-Agent de scan',
              },
            },
            {
              param: 'user-agent',
              driveUid: 'F028520F83CB40BD8EED8D20845B64AC',
              type: 'h',
              belief: 10000,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Navigateur ancien',
                description: 'pénalise les anciennes versions de navigateurs',
                category: 'User-Agent suspect',
              },
            },
          ],
          ipReputation: 12589,
          rt: 20000,
          mainAttackLabel: 'User-Agent de scan',
          mainAttack: {
            id: 54,
            uid: null,
            name: 'User-Agent de scan',
            hidden: false,
          },
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 5.0) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; Bytespider; spider-feedback@bytedance.com)',
            accept:
              'text/html,application/xhtml+xml,application/xml;q=0.9,image/heif,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7',
            'accept-encoding': 'gzip, deflate, br, zstd',
            'accept-language': 'en-US,en;q=0.5',
            priority: 'u=0, i',
            'sec-fetch-dest': 'document',
            'sec-fetch-mode': 'navigate',
            'sec-fetch-site': 'none',
            'sec-fetch-user': '?1',
            'upgrade-insecure-requests': '1',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 15:35:49 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 14.642297,
          contentLength: 4912,
          url: 'https://lescomptesdelacrypt.com/articles/9665880/attention-required--cloudflare.html',
          responseCode: 200,
        },
        {
          cause: 'geoBlocked',
          action: 'blocked',
          requestInfo: {
            scheme: 'http',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/.env',
            method: 'GET',
          },
          clientIP: '176.65.132.217',
          clientPort: '6968',
          site: 'tests-non-reg-geoblocked.protectyour.store',
          dateMilli: 1779704434487,
          cache: 'NONE',
          rt: 0,
          requestHeaders: {
            host: 'tests-non-reg-geoblocked.protectyour.store',
            referer: 'http://tests-non-reg-geoblocked.protectyour.store/',
            'user-agent':
              'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36 Edg/131.0.0.0',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
            'accept-encoding': 'gzip, br, deflate',
            'accept-language': 'en-US,en;q=0.5',
            'cache-control': 'no-cache',
            'upgrade-insecure-requests': '1',
          },
          responseHeaders: {
            'content-length': '372',
            'content-type': 'text/plain; charset=utf-8',
            date: 'Wed, 25 Feb 2026 14:46:14 GMT',
          },
          auditMode: false,
          countryCode: 'DE',
          responseTimeMs: {
            source: '0.0',
            parsedValue: 0,
          },
          contentLength: 0,
          url: 'http://tests-non-reg-geoblocked.protectyour.store/.env',
          responseCode: 403,
          botUA: false,
        },
        {
          cause: 'blacklistedIp',
          action: 'blocked',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/fr/encourager-la-montee-de-lait',
            method: 'GET',
          },
          clientIP: '51.68.88.222',
          clientPort: '33692',
          site: 'stub.ogosecurity.com',
          responseHeaders: {
            'x-ogo-blocked': 'EXCEPT',
          },
          dateMilli: 1777141288676,
          cache: 'NONE',
          requestHeaders: {
            host: 'stub.ogosecurity.com',
            'user-agent': 'Symfony HttpClient (Curl)',
            accept: '*/*',
            'accept-encoding': 'gzip',
            http_user_agent:
              'Mozilla/5.0 (Windows; U; Windows NT 5.1; en-US; rv:1.8.1.13) Gecko/20080311 Firefox/2.0.0.13',
            'x-ogo-original-host': 'agir-pour-bebe.fr',
            'x-ogo-original-x-forwarded-for': '51.68.88.222',
            'x-ogo-stub-status': '301',
          },
          countryCode: 'FR',
          responseTimeMs: {
            source: '0.0',
            parsedValue: 0,
          },
          contentLength: 10,
          url: 'https://stub.ogosecurity.com/fr/encourager-la-montee-de-lait',
          responseCode: 403,
          botUA: false,
        },
        {
          cause: 'blockedByRule',
          action: 'blocked',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/policy-deny/except-1.1.1.1',
            method: 'GET',
          },
          clientIP: '3.3.3.3',
          ruleId: '8091',
          clientPort: '50184',
          site: 'tests-non-reg.protectyour.store',
          dateMilli: 1774453571898,
          cache: 'NONE',
          requestHeaders: {
            'user-agent':
              'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/111.0.0.0 Safari/537.36',
            host: 'tests-non-reg.protectyour.store',
            accept: '*/*',
          },
          responseHeaders: {
            'content-type': 'text/plain; charset=utf-8',
            'x-content-type-options': 'nosniff',
          },
          countryCode: 'US',
          responseTimeMs: {
            source: '0.0',
            parsedValue: 0,
          },
          contentLength: 9,
          url: 'https://tests-non-reg.protectyour.store/policy-deny/except-1.1.1.1',
          responseCode: 403,
          rulePriority: 9,
          botUA: false,
        },
        {
          cause: 'banned',
          action: 'blocked',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/sito/wp-includes/wlwmanifest.xml',
            method: 'GET',
          },
          clientIP: '169.150.203.197',
          clientPort: '46374',
          site: 'api-stg.ogosecurity.com',
          dateMilli: 1772035730330,
          cache: 'NONE',
          rt: 0,
          mainAttackLabel: 'Comportement malveillant',
          mainAttack: {
            id: null,
            uid: null,
            name: 'Comportement malveillant',
            hidden: false,
          },
          requestHeaders: {
            host: 'api-stg.ogosecurity.com',
            'user-agent':
              'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/78.0.3904.108 Safari/537.36',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-language': 'en-US,en;q=0.5',
            connection: 'keep-alive',
            'keep-alive': '300',
          },
          responseHeaders: {
            'content-length': '426',
            'content-type': 'text/plain; charset=utf-8',
            date: 'Wed, 25 Feb 2026 16:08:50 GMT',
          },
          auditMode: false,
          countryCode: 'US',
          responseTimeMs: {
            source: '0.0',
            parsedValue: 0,
          },
          contentLength: 0,
          url: 'https://api-stg.ogosecurity.com/sito/wp-includes/wlwmanifest.xml',
          responseCode: 403,
          botUA: false,
        },
        {
          cause: 'cdnBlocked',
          action: 'blocked',
          cache: 'NONE',
          site: 'www.ogosecurity.com',
          clientIP: '212.30.33.46',
          dateMilli: 1769316876000,
          responseHeaders: {
            'content-length': '548',
            'content-type': 'text/html',
          },
          requestInfo: {
            method: 'GET',
            scheme: 'http',
            'content-size': 547,
            protocol: 'HTTP/1.1',
            'request-uri': '/xmlrpc.php',
          },
          requestHeaders: {
            'user-agent':
              'Mozilla/5.0 (Windows NT 6.1; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/80.0.3987.149 Safari/537.36',
            'x-forwarded-proto': 'http',
            'x-forwarded-for': '212.30.33.46',
            host: 'www.ogosecurity.com',
          },
          countryCode: 'ES',
          responseTimeMs: {
            source: '0.0',
            parsedValue: 0,
          },
          contentLength: 725,
          url: 'http://www.ogosecurity.com/xmlrpc.php',
          responseCode: 403,
          botUA: false,
        },
        {
          cause: 'challengeEmitted',
          action: 'blocked',
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/',
            method: 'GET',
          },
          clientIP: '35.193.38.120',
          ruleId: '8346',
          clientPort: '56196',
          site: 'tests-challenge-forced.protectyour.store',
          responseHeaders: {
            'content-type': 'text/html; charset=utf-8',
          },
          dateMilli: 1772036115229,
          cache: 'NONE',
          rt: 0,
          requestHeaders: {
            referer: 'http://tests-challenge-forced.protectyour.store',
            'user-agent': 'Mozilla/5.0 (compatible; CMS-Checker/1.0; +https://example.com)',
            host: 'tests-challenge-forced.protectyour.store',
            'accept-encoding': 'gzip',
          },
          auditMode: false,
          countryCode: 'US',
          responseTimeMs: {
            source: '0.0',
            parsedValue: 0,
          },
          contentLength: 5579,
          url: 'https://tests-challenge-forced.protectyour.store/',
          responseCode: 403,
          rulePriority: 1,
          botUA: false,
        },
        {
          cause: 'acmeChallenge',
          action: 'replied',
          requestInfo: {
            scheme: 'http',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/.well-known/acme-challenge/PPdFPqMXDnPnIQT0pIVHnuD_1Nysd0MOODrNUSxKh1U',
            method: 'GET',
          },
          clientIP: '13.212.77.146',
          clientPort: '52076',
          site: 'owasp.protectyour.store',
          dateMilli: 1771938029643,
          cache: 'NONE',
          requestHeaders: {
            host: 'owasp.protectyour.store',
            'user-agent': "Mozilla/5.0 (compatible; Let's Encrypt validation server; +https://www.letsencrypt.org)",
            accept: '*/*',
            'accept-encoding': 'gzip',
            connection: 'close',
          },
          responseHeaders: {
            'content-type': 'text/plain;charset=UTF-8',
            'content-length': '87',
            'access-control-allow-credentials': 'true',
            'access-control-allow-headers':
              'Origin, Accept, X-Requested-With, Content-Type, Access-Control-Request-Method, Access-Control-Request-Headers, Cookie, lang',
            'access-control-allow-methods': 'POST, GET, HEAD, OPTIONS',
            'access-control-allow-origin': 'https://d1-stg.ogosecurity.com',
            'access-control-expose-headers': 'Content-Disposition, Set-Cookie',
            'cache-control': 'no-cache, no-store, max-age=0, must-revalidate',
            date: 'Tue, 24 Feb 2026 13:00:29 GMT',
            'strict-transport-security': 'max-age=31536000',
            'x-content-type-options': 'nosniff',
            'x-frame-options': 'Deny',
            'x-xss-protection': '1; mode=block',
          },
          countryCode: 'SG',
          responseTimeMs: 16.84419,
          contentLength: 87,
          url: 'http://owasp.protectyour.store/.well-known/acme-challenge/PPdFPqMXDnPnIQT0pIVHnuD_1Nysd0MOODrNUSxKh1U',
          responseCode: 200,
          botUA: false,
        },
        {
          cause: 'challenge',
          requestInfo: {
            'content-size': 0,
            protocol: 'HTTP/1.1',
            scheme: 'https',
            'query-string': 'difficulty=C2',
            'request-uri': '/.well-known/ogo-challenge/page',
            method: 'GET',
          },
          clientIP: '2a01:cb00:3029:5ac2:243e:8013:9e11:923d',
          clientPort: '36698',
          action: 'replied',
          site: 'stub3.ogosecurity.com',
          dateMilli: 1772037626896,
          cache: 'NONE',
          requestHeaders: {
            cookie:
              '_ga=GA1.1.88239317.1763550874; hubspotutk=9b902276e1c1445bde5f8fce392f10a9; _groove_session_2=_OGO_DELETED_; __hssrc=1; _ga_VCDM38G24G=GS2.1.s1768565360$o4$g0$t1768565360$j60$l0$h0; _ga_38HPP2MJ2B=GS2.1.s1770978869$o8$g0$t1770978869$j60$l0$h0; ph_phc_rsG2vmLJv0UVjxF7AIiZZ3eSFJClXm3aBxnaskxL4KC_posthog=%7B%22distinct_id%22%3A%22019c3339-e1e4-75e8-8c23-2727d6182330%22%2C%22%24sesid%22%3A%5B1770978877513%2C%22019c5691-1520-7db4-9f43-e6e423e103f5%22%2C1770978874655%5D%2C%22%24initial_person_info%22%3A%7B%22r%22%3A%22%24direct%22%2C%22u%22%3A%22https%3A%2F%2Faide.ogosecurity.com%2Fhelp%2Fcache-cdn%22%7D%7D; ogo_session=_OGO_DELETED_',
            host: 'stub3.ogosecurity.com',
            referer: 'https://stub3.ogosecurity.com//challenge',
            'user-agent':
              'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/145.0.0.0 Safari/537.36',
            accept:
              'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7',
            'accept-encoding': 'gzip, deflate, br, zstd',
            'accept-language': 'en-US,en;q=0.9,fr-FR;q=0.8,fr;q=0.7',
            'cdn-loop': 'nb1d2; c=09',
            connection: 'upgrade',
            priority: 'u=0, i',
            'sec-ch-ua': '"Not:A-Brand";v="99", "Google Chrome";v="145", "Chromium";v="145"',
            'sec-ch-ua-mobile': '?0',
            'sec-ch-ua-platform': '"macOS"',
            'sec-fetch-dest': 'iframe',
            'sec-fetch-mode': 'navigate',
            'sec-fetch-site': 'same-origin',
            traceparent: '00-32cf62cb60531166b5f9f427e56a5429-357e925a2115aa4d-01',
            'upgrade-insecure-requests': '1',
            'x-cdn-node-addr': '2a01:c9c0:c008::',
            'x-cdn-real-host': 'stub3.ogosecurity.com',
            'x-cdn-requestor': 'parf20-hw-edge-or0003',
            'x-forwarded-prefix': '/.well-known/ogo-challenge',
            'x-tcpinfo-rtt': '9739',
          },
          responseHeaders: {
            'content-type': 'text/html; charset=utf-8',
            'access-control-allow-headers': '*',
            'access-control-allow-methods': 'GET, POST, OPTIONS',
            'access-control-allow-origin': '*',
            'cache-control': 'no-store, no-cache, must-revalidate',
            date: 'Wed, 25 Feb 2026 16:40:26 GMT',
            expires: '0',
            pragma: 'no-cache',
            vary: 'Accept-Encoding',
          },
          countryCode: 'FR',
          responseTimeMs: 1.008159,
          contentLength: 6906,
          url: 'https://stub3.ogosecurity.com/.well-known/ogo-challenge/page?difficulty=C2',
          responseCode: 200,
          botUA: false,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/articles/9671363/le-bitcoin-se-stabilise-autour-de-ses-points-bas.html',
            method: 'GET',
          },
          clientIP: '5.102.173.71',
          clientPort: '33288',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026296544,
          cache: 'NONE',
          botUA: true,
          drives: [
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 99,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            'user-agent': 'Mozilla/5.0 (compatible; MojeekBot/0.11; +https://www.mojeek.com/bot.html)',
            host: 'lescomptesdelacrypt.com',
            accept: '*/*',
            'accept-encoding': 'deflate, gzip',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:31:36 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'GB',
          responseTimeMs: 10.877807,
          contentLength: 5067,
          url: 'https://lescomptesdelacrypt.com/articles/9671363/le-bitcoin-se-stabilise-autour-de-ses-points-bas.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/robots.txt',
            method: 'GET',
          },
          clientIP: '5.102.173.71',
          clientPort: '33288',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026296494,
          cache: 'NONE',
          botUA: true,
          drives: [
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 99,
              confidence: 90000,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            'user-agent': 'Mozilla/5.0 (compatible; MojeekBot/0.11; +https://www.mojeek.com/bot.html)',
            host: 'lescomptesdelacrypt.com',
            accept: '*/*',
            'accept-encoding': 'deflate, gzip',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:31:36 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'GB',
          responseTimeMs: 12.743384,
          contentLength: 140,
          url: 'https://lescomptesdelacrypt.com/robots.txt',
          responseCode: 404,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri':
              '/articles/9671374/actualits-sur-la-trsorerie-bitcoin--la-fusion-spac-dx27adam-back-avec-cantor-equity-partners-pourrait-intervenir-ds-avril.html',
            method: 'GET',
          },
          clientIP: '94.23.188.206',
          clientPort: '55492',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026287366,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            'user-agent': 'Mozilla/5.0 (compatible; AhrefsBot/7.0; +http://ahrefs.com/robot/)',
            host: 'lescomptesdelacrypt.com',
            accept: '*/*',
            'accept-encoding': 'deflate, gzip, br, zstd',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:31:27 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'FR',
          responseTimeMs: 15.447666,
          contentLength: 5311,
          url: 'https://lescomptesdelacrypt.com/articles/9671374/actualits-sur-la-trsorerie-bitcoin--la-fusion-spac-dx27adam-back-avec-cantor-equity-partners-pourrait-intervenir-ds-avril.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/articles/9631337/attention-required--cloudflare.html',
            method: 'GET',
          },
          clientIP: '114.119.146.40',
          clientPort: '23611',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026275553,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            referer: 'https://lescomptesdelacrypt.com/articles/9631337/attention-required--cloudflare.html',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-encoding': 'gzip, deflate',
            'accept-language': 'en',
            connection: 'Close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:31:15 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 15.047737,
          contentLength: 4920,
          url: 'https://lescomptesdelacrypt.com/articles/9631337/attention-required--cloudflare.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/robots.txt',
            method: 'GET',
          },
          clientIP: '74.7.244.42',
          clientPort: '51134',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026245000,
          cache: 'NONE',
          botUA: true,
          drives: [
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 199,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            'user-agent':
              'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36; compatible; OAI-SearchBot/1.3; robots.txt; +https://openai.com/searchbot',
            accept: '*/*',
            'accept-encoding': 'gzip, br, deflate',
            from: 'oai-searchbot(at)openai.com',
            'x-openai-host-hash': '317246727',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:30:45 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'US',
          responseTimeMs: 12.066684,
          contentLength: 198,
          url: 'https://lescomptesdelacrypt.com/robots.txt',
          responseCode: 404,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri':
              '/articles/9637156/rserve-de-bitcoin-usa-le-congrs-veut-un-rapport-sur-la-faisabilit-et-la-scurit.html',
            method: 'GET',
          },
          clientIP: '114.119.159.233',
          clientPort: '52371',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026192154,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            referer:
              'https://lescomptesdelacrypt.com/articles/9637156/rserve-de-bitcoin-usa-le-congrs-veut-un-rapport-sur-la-faisabilit-et-la-scurit.html',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-encoding': 'gzip, deflate',
            'accept-language': 'en',
            connection: 'Close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:29:52 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 14.229482,
          contentLength: 5238,
          url: 'https://lescomptesdelacrypt.com/articles/9637156/rserve-de-bitcoin-usa-le-congrs-veut-un-rapport-sur-la-faisabilit-et-la-scurit.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/articles/9671394/attention-required--cloudflare.html',
            method: 'GET',
          },
          clientIP: '92.222.108.111',
          clientPort: '41844',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026155514,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            'user-agent': 'Mozilla/5.0 (compatible; AhrefsBot/7.0; +http://ahrefs.com/robot/)',
            host: 'lescomptesdelacrypt.com',
            accept: '*/*',
            'accept-encoding': 'deflate, gzip, br, zstd',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:29:15 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'FR',
          responseTimeMs: 15.452736,
          contentLength: 4932,
          url: 'https://lescomptesdelacrypt.com/articles/9671394/attention-required--cloudflare.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri':
              '/articles/9638190/chipper-cash-dveloppe-lightning-en-afrique--plus-de-50--des-transactions-bitcoin-dsormais-sur-le-rseau-o-bitcoin-news.html',
            method: 'GET',
          },
          clientIP: '114.119.146.40',
          clientPort: '23607',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026101793,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            referer:
              'https://lescomptesdelacrypt.com/articles/9638190/chipper-cash-dveloppe-lightning-en-afrique--plus-de-50--des-transactions-bitcoin-dsormais-sur-le-rseau-o-bitcoin-news.html',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-encoding': 'gzip, deflate',
            'accept-language': 'en',
            connection: 'Close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:28:21 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 15.052621,
          contentLength: 5218,
          url: 'https://lescomptesdelacrypt.com/articles/9638190/chipper-cash-dveloppe-lightning-en-afrique--plus-de-50--des-transactions-bitcoin-dsormais-sur-le-rseau-o-bitcoin-news.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri':
              '/articles/9671265/meta-relance-ses-projets-de-stablecoin--stripe-pressenti-pour-piloter-les-paiements.html',
            method: 'GET',
          },
          clientIP: '43.159.152.184',
          clientPort: '49982',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026057873,
          cache: 'NONE',
          drives: [
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 199,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            'user-agent':
              'Mozilla/5.0 (iPhone; CPU iPhone OS 13_2_3 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.0.3 Mobile/15E148 Safari/604.1',
            accept:
              'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7',
            'accept-encoding': 'gzip',
            'accept-language': 'zh-CN,zh;q=0.9,en-US;q=0.8,en;q=0.7',
            'cache-control': 'no-cache',
            connection: 'keep-alive',
            pragma: 'no-cache',
            'upgrade-insecure-requests': '1',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:27:37 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'US',
          responseTimeMs: 15.159447,
          contentLength: 5111,
          url: 'https://lescomptesdelacrypt.com/articles/9671265/meta-relance-ses-projets-de-stablecoin--stripe-pressenti-pour-piloter-les-paiements.html',
          responseCode: 200,
          botUA: false,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri': '/articles/9671396/attention-required--cloudflare.html',
            method: 'GET',
          },
          clientIP: '51.75.236.134',
          clientPort: '31828',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026017027,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            'user-agent': 'Mozilla/5.0 (compatible; AhrefsBot/7.0; +http://ahrefs.com/robot/)',
            host: 'lescomptesdelacrypt.com',
            accept: '*/*',
            'accept-encoding': 'deflate, gzip, br, zstd',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:26:57 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'FR',
          responseTimeMs: 13.705521,
          contentLength: 4943,
          url: 'https://lescomptesdelacrypt.com/articles/9671396/attention-required--cloudflare.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri':
              '/articles/9641906/dernires-nouvelles-sur-ripple-xrpa-xrp-tundra-lance-une-prvente-rvolutionnaire-de-jetons-doubles--0.041a-pour-atteindre-2.50a-financefeeds.html',
            method: 'GET',
          },
          clientIP: '114.119.140.137',
          clientPort: '35309',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772026012605,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            referer:
              'https://lescomptesdelacrypt.com/articles/9641906/dernires-nouvelles-sur-ripple-xrpa-xrp-tundra-lance-une-prvente-rvolutionnaire-de-jetons-doubles--0.041a-pour-atteindre-2.50a-financefeeds.html',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-encoding': 'gzip, deflate',
            'accept-language': 'en',
            connection: 'Close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:26:52 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 16.612251,
          contentLength: 5317,
          url: 'https://lescomptesdelacrypt.com/articles/9641906/dernires-nouvelles-sur-ripple-xrpa-xrp-tundra-lance-une-prvente-rvolutionnaire-de-jetons-doubles--0.041a-pour-atteindre-2.50a-financefeeds.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri':
              '/articles/9658618/letos-talk-avec-hein-lannoy--la-big-data--un-instrument-stratgique-vbo-feb-verbond-van-belgische-ondernemingen-fdration-des-entreprises-de-belgique.html',
            method: 'GET',
          },
          clientIP: '185.191.171.9',
          clientPort: '32508',
          action: 'authorized',
          site: 'www.lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772025996689,
          cache: 'NONE',
          botUA: true,
          drives: [
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 199,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'www.lescomptesdelacrypt.com',
            'user-agent': 'Mozilla/5.0 (compatible; SemrushBot/7~bl; +http://www.semrush.com/bot.html)',
            accept:
              'text/html, application/rss+xml, application/atom+xml, text/xml, text/rss+xml, application/xhtml+xml',
            'accept-encoding': 'gzip,deflate',
            connection: 'close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:26:36 GMT',
          },
          auditMode: true,
          countryCode: 'GB',
          responseTimeMs: 14.184929,
          contentLength: 5314,
          url: 'https://www.lescomptesdelacrypt.com/articles/9658618/letos-talk-avec-hein-lannoy--la-big-data--un-instrument-stratgique-vbo-feb-verbond-van-belgische-ondernemingen-fdration-des-entreprises-de-belgique.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/robots.txt',
            method: 'GET',
          },
          clientIP: '85.208.96.194',
          clientPort: '19466',
          action: 'authorized',
          site: 'www.lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772025996276,
          cache: 'NONE',
          botUA: true,
          drives: [
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 199,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'www.lescomptesdelacrypt.com',
            'user-agent': 'Mozilla/5.0 (compatible; SemrushBot/7~bl; +http://www.semrush.com/bot.html)',
            accept: '*/*',
            'accept-encoding': 'gzip,deflate',
            connection: 'close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:26:36 GMT',
          },
          auditMode: true,
          countryCode: 'US',
          responseTimeMs: 11.915731,
          contentLength: 140,
          url: 'https://www.lescomptesdelacrypt.com/robots.txt',
          responseCode: 404,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri': '/',
            method: 'GET',
          },
          clientIP: '205.210.31.181',
          clientPort: '58534',
          action: 'authorized',
          site: 'www.lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772025965492,
          cache: 'NONE',
          drives: [
            {
              param: '',
              driveUid: 'DA94FC29DEBA406493D4B871AB6732E0',
              type: '',
              belief: 199,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Catégorie OGO : Malware / Botnet / Command & Control',
                description: "pénaliser les IPs provenant de réseaux C2 (Command & Control) connus d'OGO",
                category: 'Catégorie OGO : Malware / Botnet / Command & Control',
              },
            },
          ],
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            'user-agent':
              'Hello from Palo Alto Networks, find out more about our scans in https://docs-cortex.paloaltonetworks.com/r/1/Cortex-Xpanse/Scanning-activity',
            host: 'www.lescomptesdelacrypt.com',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            date: 'Wed, 25 Feb 2026 13:26:05 GMT',
          },
          auditMode: true,
          countryCode: 'US',
          responseTimeMs: 28.765929,
          contentLength: 112377,
          url: 'https://www.lescomptesdelacrypt.com/',
          responseCode: 200,
          botUA: false,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri':
              '/articles/9664180/scandale--le-gouvernement-amricain-s039est-il-fait-voler-plus-de-40-millions-de-dollars-en-cryptomonnaies-saisies-.html',
            method: 'GET',
          },
          clientIP: '47.128.97.164',
          clientPort: '62124',
          action: 'blocked',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedKO',
          dateMilli: 1772025945914,
          cache: 'NONE',
          botUA: true,
          drives: [
            {
              param: 'user-agent',
              driveUid: 'E5B0316FE406445585FB89F28874ECED',
              type: 'h',
              belief: 100000,
              label: {
                label: 'User-Agent de scan',
                description: 'pénaliser les requêtes présentant un User-Agent de scan (ex: nmap)',
                category: 'User-Agent de scan',
              },
            },
            {
              param: 'user-agent',
              driveUid: 'F028520F83CB40BD8EED8D20845B64AC',
              type: 'h',
              belief: 10000,
              label: {
                label:
                  '<label class="badge badge-outline badge-outline-secondary">Facteur aggravant</label> Navigateur ancien',
                description: 'pénalise les anciennes versions de navigateurs',
                category: 'User-Agent suspect',
              },
            },
          ],
          ipReputation: 0,
          rt: 20000,
          mainAttackLabel: 'User-Agent de scan',
          mainAttack: { id: 54, uid: null, name: 'User-Agent de scan', hidden: false },
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 5.0) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; Bytespider; spider-feedback@bytedance.com)',
            accept:
              'text/html,application/xhtml+xml,application/xml;q=0.9,image/heif,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7',
            'accept-encoding': 'gzip, deflate, br, zstd',
            'accept-language': 'en-US,en;q=0.5',
            priority: 'u=0, i',
            'sec-fetch-dest': 'document',
            'sec-fetch-mode': 'navigate',
            'sec-fetch-site': 'none',
            'sec-fetch-user': '?1',
            'upgrade-insecure-requests': '1',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:25:45 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 15.334449,
          contentLength: 5126,
          url: 'https://lescomptesdelacrypt.com/articles/9664180/scandale--le-gouvernement-amricain-s039est-il-fait-voler-plus-de-40-millions-de-dollars-en-cryptomonnaies-saisies-.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri':
              '/articles/9642736/le-rebond-du-cours-du-bitcoin-ravive-la-confiance-du-march-alors-que-les-marchs-alternatifs-stagnent-the-market-periodical.html',
            method: 'GET',
          },
          clientIP: '114.119.133.119',
          clientPort: '54053',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772025927904,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            referer: 'https://lescomptesdelacrypt.com/opinions/tribunes/les-enjeux-economiques-du-bitcoin-760559.html',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-encoding': 'gzip, deflate',
            'accept-language': 'en',
            connection: 'Close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:25:27 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 14.367085,
          contentLength: 5250,
          url: 'https://lescomptesdelacrypt.com/articles/9642736/le-rebond-du-cours-du-bitcoin-ravive-la-confiance-du-march-alors-que-les-marchs-alternatifs-stagnent-the-market-periodical.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/2.0',
            'content-size': 0,
            'request-uri':
              '/articles/9671397/un-initi-dment-la-fuite-sur-gta-6-selon-laquelle-rockstar-utilisera-des-crypto-monnaies-comme-moyen-de-paiement-pour-les-missions.html',
            method: 'GET',
          },
          clientIP: '5.39.109.169',
          clientPort: '59292',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772025860863,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            'user-agent': 'Mozilla/5.0 (compatible; AhrefsBot/7.0; +http://ahrefs.com/robot/)',
            host: 'lescomptesdelacrypt.com',
            accept: '*/*',
            'accept-encoding': 'deflate, gzip, br, zstd',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:24:20 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'FR',
          responseTimeMs: 14.680997,
          contentLength: 5307,
          url: 'https://lescomptesdelacrypt.com/articles/9671397/un-initi-dment-la-fuite-sur-gta-6-selon-laquelle-rockstar-utilisera-des-crypto-monnaies-comme-moyen-de-paiement-pour-les-missions.html',
          responseCode: 200,
        },
        {
          requestInfo: {
            scheme: 'https',
            protocol: 'HTTP/1.1',
            'content-size': 0,
            'request-uri':
              '/articles/9655397/la-fondation-ethereum-dvoile-eainterop-layerau--un-cosystme-de-layer-2-unifi-comme-une-seule-blockchain.html',
            method: 'GET',
          },
          clientIP: '114.119.140.137',
          clientPort: '35307',
          action: 'authorized',
          site: 'lescomptesdelacrypt.com',
          cause: 'analyzedOK',
          dateMilli: 1772025839491,
          cache: 'NONE',
          botUA: true,
          ipReputation: 100000,
          rt: 20000,
          requestHeaders: {
            host: 'lescomptesdelacrypt.com',
            referer:
              'https://lescomptesdelacrypt.com/articles/9655397/la-fondation-ethereum-dvoile-eainterop-layerau--un-cosystme-de-layer-2-unifi-comme-une-seule-blockchain.html',
            'user-agent':
              'Mozilla/5.0 (Linux; Android 7.0;) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)',
            accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'accept-encoding': 'gzip, deflate',
            'accept-language': 'en',
            connection: 'Close',
          },
          responseHeaders: {
            server: 'nginx/1.6.2 (Ubuntu)',
            'content-type': 'text/html; charset=UTF-8',
            'content-encoding': 'gzip',
            date: 'Wed, 25 Feb 2026 13:23:59 GMT',
            'strict-transport-security': 'max-age=31536000',
          },
          auditMode: true,
          countryCode: 'SG',
          responseTimeMs: 13.073655,
          contentLength: 5187,
          url: 'https://lescomptesdelacrypt.com/articles/9655397/la-fondation-ethereum-dvoile-eainterop-layerau--un-cosystme-de-layer-2-unifi-comme-une-seule-blockchain.html',
          responseCode: 200,
        },
      ],
      pageable: {
        pageNumber: 0,
        pageSize: 20,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: 0,
        paged: true,
        unpaged: false,
      },
      last: false,
      totalElements: 513711,
      totalPages: 25686,
      sort: { sorted: false, unsorted: true, empty: true },
      first: true,
      size: 20,
      number: 0,
      numberOfElements: 20,
      empty: false,
    });
  }
  // END-NOSCAN

  downloadLogs(request: LogRequest, context: ContextEnum): Observable<Blob> {
    const params = buildHttpParams(request);

    return this.http
      .get(this.getLogsEndpoint('download', context), {
        params,
        reportProgress: true,
        observe: 'response',
        responseType: 'blob' as 'json',
      })
      .pipe(map((res) => new Blob([res.body as any], { type: 'json' })));
  }

  downloadLogsURL(request: LogRequest, context: ContextEnum): string {
    const params = buildHttpParams(request);
    return this.getLogsEndpoint('download', context) + '?' + params;
  }

  getAttacksCategories(): Observable<AttackCategory[]> {
    return this.http.get<AttackCategory[]>('v2/attack-categories');
  }

  getBlockedByAttackCategory(request: LogRequest, context: ContextEnum): Observable<DatasWithTotal<DrillDown>> {
    if (!this.config.mockDashboardData) {
      return this.http
        .get<DatasWithTotal<DrillDown>>(this.getLogsEndpoint('by-attack-category', context), {
          params: buildHttpParams(request),
        })
        .pipe(takeUntil(this.cancelPendingRequests$));
    } else {
      return this.getBlockedByAttackCategoryMockData().pipe(delay(this.config.mockDataDelay));
    }
  }

  // BEGIN-NOSCAN
  private getBlockedByAttackCategoryMockData(): Observable<DatasWithTotal<DrillDown>> {
    console.log('mockData getBlockedByAttackCategory');
    return of({
      datas: [
        {
          id: 69,
          uid: '24e2b4d3-f23b-4eb9-b929-c8374604de33',
          name: 'Injection PHP',
          y: 1,
        },
        {
          id: 54,
          uid: '83042f42-c4be-4570-9096-adc1d42738ae',
          name: 'User-Agent de scan',
          y: 40,
        },
        {
          id: 15,
          uid: '7f3395da-ba79-4926-ad4e-18208a45e817',
          name: 'Attaque par force brute',
          y: 1,
        },
        {
          id: 18,
          uid: '8c600638-1409-4504-b0c3-9a22a9b2a283',
          name: 'Accès à un fichier sensible',
          y: 7,
        },
      ],
      total: 49,
    });
  }

  // END-NOSCAN

  getTopBlockedIps(request: LogRequest & Partial<AggSize>, context: ContextEnum): Observable<BlockedIp[]> {
    const pipe = this.config.mockDashboardData
      ? of(mockDataTopBlockedIps).pipe(delay(this.config.mockDataDelay))
      : this.http.get<BlockedIpData[]>(this.getLogsEndpoint('top-blocked-ips', context), {
          params: buildHttpParams(request),
        });

    return pipe.pipe(takeUntil(this.cancelPendingRequests$)).pipe(
      map((datas) =>
        _.flatMap(datas, (blockedIp) =>
          blockedIp.siteStats.map((siteStats) => ({
            ip: blockedIp.ip,
            countryCode: blockedIp.countryCode,
            ...siteStats,
          })),
        ),
      ),
    );
  }

  getSlowUrls(request: LogRequest & Partial<AggSize>, context: ContextEnum): Observable<UrlStats[]> {
    return this.http
      .get<UrlStats[]>(this.getLogsEndpoint('slow-urls', context), { params: buildHttpParams(request) })
      .pipe(takeUntil(this.cancelPendingRequests$));
  }

  getCpuConsumingUrls(request: LogRequest & Partial<AggSize>, context: ContextEnum): Observable<UrlStats[]> {
    return this.http
      .get<UrlStats[]>(this.getLogsEndpoint('cpu-consuming-urls', context), {
        params: buildHttpParams(request),
      })
      .pipe(takeUntil(this.cancelPendingRequests$));
  }

  getDateHistograms<T extends keyof any, D>(
    graphType: GraphType,
    request: DateHistogramLogRequest,
    context: ContextEnum,
  ): Observable<DateHistograms<T, D>> {
    let response: Observable<DateHistograms<T, D>>;
    if (this.config.mockDashboardData) {
      console.log(`mockData getDateHistograms ${graphType}`, request);
      response = of(dateHistogramMockData[graphType]).pipe(delay(this.config.mockDataDelay)) as Observable<
        DateHistograms<T, D>
      >;
    } else {
      const DateHistogramEndpoints: { [key in GraphType]: string } = {
        // have to do this otherwise it was buggy
        [TRAFIC]: 'traffic',
        [RESPONSE_TIME]: 'response-time',
        [RESPONSES_CODES]: 'response-code',
        [BANDWIDTH]: 'bandwidth',
      };

      response = this.http
        .get<DateHistograms<T, D>>(this.getLogsEndpoint(DateHistogramEndpoints[graphType], context), {
          params: buildHttpParams(request),
        })
        .pipe(takeUntil(this.cancelPendingRequests$));
    }

    return response.pipe(
      map(
        (res) =>
          // TODO remove key mapping
          _.mapValues(res, (histogram) =>
            _.mapKeys(histogram, (_, k) => (k == 'dateHistogram' ? 'data' : k)),
          ) as DateHistograms<T, D>,
      ),
    );
  }

  getTrafficByCountry(request: LogRequest, context: ContextEnum): Observable<TrafficByCountry> {
    if (this.config.mockDashboardData) {
      return of({
        traffic: [
          { name: 'FR', y: 123500 },
          { name: 'CA', y: 12350 },
          { name: 'NO', y: 635 },
          { name: 'SG', y: 3 },
        ],
        blockedByBrain: [
          { name: 'FR', y: 1500 },
          { name: 'CA', y: 350 },
          { name: 'SG', y: 15 },
        ],
        blockedCountry: [
          { name: 'FR', y: 1500 },
          { name: 'CA', y: 350 },
          { name: 'SG', y: 15 },
        ],
        robot: [
          { name: 'FR', y: 1500 },
          { name: 'CA', y: 350 },
          { name: 'SG', y: 15 },
        ],
      }).pipe(delay(this.config.mockDataDelay)) as Observable<TrafficByCountry>;
    }

    return this.http
      .get<TrafficByCountry>(this.getLogsEndpoint('traffic/by-country', context), {
        params: buildHttpParams(request),
      })
      .pipe(takeUntil(this.cancelPendingRequests$));
  }

  getTrafficBreakdown(
    suffix: string,
    request: LogRequest,
    context: ContextEnum,
  ): Observable<(SiteTrafficBreakdown & Rps)[]> {
    const periodDuration = new DateRange(request.fromDate, request.toDate).length('seconds');
    let response: Observable<SiteTrafficBreakdown[]>;
    if (!this.config.mockDashboardData) {
      response = this.getBreakdown<SiteTrafficBreakdown>(context, suffix, request);
    } else {
      let mockData;
      if (suffix.includes('top-ips')) {
        mockData = [
          {
            total: 1626,
            blocked: 138,
            suspect: 15,
            key: '34.153.175.217',
            countryCode: 'GB',
          },
          {
            total: 798,
            blocked: 19,
            suspect: 0,
            key: '185.191.171.11',
            countryCode: 'FR',
          },
        ];
      } else {
        mockData = [
          {
            site: 'lescomptesdelacrypt.com',
            total: 1626,
            blocked: 138,
            suspect: 15,
          },
          {
            site: 'www.lescomptesdelacrypt.com',
            total: 798,
            blocked: 19,
            suspect: 0,
          },
          {
            site: 'samuelbenais.fr',
            total: 260,
            blocked: 56,
            suspect: 24,
          },
          {
            site: 'tests-non-reg.protectyour.store',
            total: 70,
            blocked: 30,
            suspect: 6,
          },
          {
            site: 'api-stg.ogosecurity.com',
            total: 48,
            blocked: 4,
            suspect: 0,
          },
        ];
      }
      response = of(mockData).pipe(delay(this.config.mockDataDelay));
    }
    return response.pipe(map((items) => items.map((item) => ({ ...item, rps: item.total / periodDuration }))));
  }

  getUrlBreakdown<T>(suffix: string, request: LogRequest, context: ContextEnum): Observable<T[]> {
    return this.getBreakdown<T>(context, suffix, request);
  }

  getBreakdown<T>(context: ContextEnum, suffix: string, request: LogRequest): Observable<T[]> {
    return this.http
      .get<T[]>(this.getLogsEndpoint(suffix, context), {
        params: buildHttpParams(request),
      })
      .pipe(takeUntil(this.cancelPendingRequests$));
  }

  private parseLog(data: any): LogEntry {
    const {
      dateMilli,
      action,
      cause,
      rulePriority,
      auditMode,
      drives,
      ipReputation,
      rewriteOriginalPath,
      rt,
      mainAttack,
      mainAttackLabel,
      ...rest
    } = data;

    rest.responseTimeMs = Math.trunc(rest.responseTimeMs * 100) / 100;

    // uncategorized requests
    if (data.action == null && data.cause == null) {
      data.action = ActionEnum.AUTHORIZED;
      data.cause = 'analyzedOK';
    }

    const log: LogEntry = {
      site: data.site,
      path: data.requestInfo['request-uri'],
      date: data.dateMilli,
      clientIP: data.clientIP,
      time: data.httpResponseMilli,
      method: data.requestInfo['method'],
      code: data.responseCode,
      countryCode: data.countryCode,
      action: data.action,
      cause: data.cause,
      cache: data.cache,
      auditMode: data.auditMode,
      rulePriority: data.rulePriority,
      drivesList: drives ? this.parseDrives(drives) : null,
      rewriteOriginalPath,
      mainAttack,
      rt: rt / 1000,
      url: data.url,
      responseTimeMs: rest.responseTimeMs,
      contentLength: data.contentLength,
      requestDetails: { ...rest },
    };

    if (_.isNumber(ipReputation)) {
      const ipReputationPercent = Math.trunc(ipReputation / CREDIBILITY_FACTOR);
      log.requestDetails.credibility = ipReputationPercent + '%';
      log.credibility = ipReputationPercent;
    }

    if (log?.requestDetails?.requestInfo['request-uri']) {
      log.requestDetails.requestInfo.path = log.requestDetails.requestInfo['request-uri'];
      delete log.requestDetails.requestInfo['request-uri'];
    }

    return log;
  }

  private parseDrives(drivesList): DrivesList {
    let highlight: string[] = [];
    let drives: DrivesListDrive[] = [];

    drivesList.forEach((d) => {
      let param = d.param;
      drives.push({
        category: d.label.category,
        label: d.label.label,
        description: d.label.description,
        confidence: Math.round(d.confidence / 1000),
      });

      switch (d.type) {
        case 'i':
          if (param == 'path-info') {
            param = 'request-uri';
          }
          highlight.push(`requestInfo.${param.toLowerCase()}`);
          break;
        case 'h':
          highlight.push(`requestHeaders.${param.toLowerCase()}`);
          break;
        case 'c':
          highlight.push(`requestHeaders.cookie`);
          break;
        case 'q':
          highlight.push('requestInfo.query-string');
          break;
        case 'mipl':
          highlight.push('clientIP');
          break;
        case 'b':
          highlight.push('body');
          break;
      }
    });

    return { drives, highlight };
  }

  private getLogsEndpoint(suffix: string, context: ContextEnum): string {
    return this.url(
      '/logs' +
        (context == ADMIN_CONTEXT ? '-admin-cluster' : '') +
        (suffix !== '' ? prependIfMissing(suffix, '/') : ''),
    );
  }
}

export const CREDIBILITY_FACTOR = 1000;

export type DateHistogram = { data: Array<[number, number]> };

export type DateHistograms<T extends keyof any, D> = { [key in T]: DateHistogram & D };

export type CountryGraphType = 'traffic' | 'blockedByBrain' | 'robot' | 'blockedCountry';

export type TrafficByCountry = Record<CountryGraphType, Array<{ name: string; y: number }>>;

export type DateHistogramLogRequest = LogRequest & { aggregationInterval: string };

export type CacheStatus = 'HIT' | 'HIT_CDN' | 'MISS' | 'NONE';

export type BandwidthHistograms = {
  contentLength: DateHistograms<string, {}>;
  cacheStatus: DateHistograms<CacheStatus, {}>;
};

export type ResponseTimeHistograms = {
  traffic: DateHistogram;
  responseTime: DateHistograms<string, {}>;
};

interface BlockedIpData {
  ip: string;
  countryCode: string;
  blocked: number;
  siteStats: BlockedIpSiteData[];
}

interface BlockedIpSiteData {
  site: string;
  total: number;
  blockedByBrain: number;
  blockedByRule: number;
  blockedByCdn: number;
  geoblocked: number;
  challengeEmitted: number;
  suspicious: number;
}

export type BlockedIp = { ip: string; countryCode: string } & BlockedIpSiteData;

export interface DatasWithTotal<T> {
  datas: T[];
  total: number;
}

export interface DrillDown {
  uid: string;
  name: string;
  y: number;
}

export interface PageParams {
  page: number;
  size: number;
}

export interface LogEntry {
  site: string;
  path: string;
  rewriteOriginalPath: string;
  url: string;
  method: string;
  code: number;
  date: number;
  clientIP: string;
  countryCode: string;
  time: number;
  responseTimeMs: number;
  contentLength: number;
  action: ActionEnum;
  cause: Cause;
  auditMode: boolean;
  rulePriority: number | string;
  cache: string;
  mainAttack: MainAttack;
  drivesList?: DrivesList;
  rt?: number;
  credibility?: number;
  requestDetails: any;
}

type MainAttack = {
  uid: string;
  name: string;
};

type DrivesList = {
  drives: DrivesListDrive[];
  highlight: string[];
};

type DrivesListDrive = { category: string; label: string; description: string; confidence: number };

export interface UrlStats {
  url: string;
  site: string;
  path: string;
  count: number;
  avg: number;
  min: number;
  max: number;
  sum: number;
}

export interface UrlBandwidth {
  url: string;
  bandwidth: { [prop in CacheStatus]: number };
}

export interface SiteBandwidth {
  site: string;
  bandwidth: Record<CacheStatus, number>;
}

export interface UrlResponseCodesCount {
  url: string;
  count: number;
  count4xx: number;
  count5xx: number;
}

export interface AggSize {
  aggSize: number;
}

export interface SiteTrafficBreakdown {
  site: string;
  total: number;
  blocked: number;
  suspect: number;
}

export interface Rps {
  rps: number;
}

// BEGIN-NOSCAN
const dateHistogramMockData: Record<GraphType, DateHistograms<any, any>> = {
  traffic: {
    traffic: {
      total: 2933,
      dateHistogram: [
        [1771974000000, 163],
        [1771975800000, 92],
        [1771977600000, 180],
        [1771979400000, 35],
        [1771981200000, 45],
        [1771983000000, 88],
        [1771984800000, 59],
        [1771986600000, 166],
        [1771988400000, 62],
        [1771990200000, 49],
        [1771992000000, 91],
        [1771993800000, 123],
        [1771995600000, 163],
        [1771997400000, 60],
        [1771999200000, 75],
        [1772001000000, 85],
        [1772002800000, 74],
        [1772004600000, 92],
        [1772006400000, 97],
        [1772008200000, 80],
        [1772010000000, 175],
        [1772011800000, 65],
        [1772013600000, 80],
        [1772015400000, 52],
        [1772017200000, 49],
        [1772019000000, 40],
        [1772020800000, 46],
        [1772022600000, 51],
        [1772024400000, 90],
        [1772026200000, 213],
        [1772028000000, 174],
        [1772029800000, 19],
      ],
      bySite: {
        'stub.ogosecurity.com': 24,
        'lescomptesdelacrypt.com': 1626,
        'owasp.protectyour.store': 16,
        other: 33,
        'www.lescomptesdelacrypt.com': 798,
        'tests-non-reg.protectyour.store': 70,
        'api-stg.ogosecurity.com': 48,
        'samuelbenais.fr': 260,
        'drupal.protectyour.store': 26,
        'wdemo.protectyour.store': 24,
        'gb.ogosecurity.com': 8,
      },
    },
    blocked: {
      total: 292,
      dateHistogram: [
        [1771974000000, 5],
        [1771975800000, 4],
        [1771977600000, 7],
        [1771979400000, 6],
        [1771981200000, 5],
        [1771983000000, 3],
        [1771984800000, 7],
        [1771986600000, 60],
        [1771988400000, 5],
        [1771990200000, 6],
        [1771992000000, 7],
        [1771993800000, 23],
        [1771995600000, 25],
        [1771997400000, 4],
        [1771999200000, 16],
        [1772001000000, 8],
        [1772002800000, 4],
        [1772004600000, 4],
        [1772006400000, 5],
        [1772008200000, 6],
        [1772010000000, 2],
        [1772011800000, 6],
        [1772013600000, 12],
        [1772015400000, 7],
        [1772017200000, 10],
        [1772019000000, 5],
        [1772020800000, 9],
        [1772022600000, 3],
        [1772024400000, 9],
        [1772026200000, 3],
        [1772028000000, 16],
        [1772029800000, 0],
      ],
      bySite: {
        'stub.ogosecurity.com': 4,
        'lescomptesdelacrypt.com': 138,
        'owasp.protectyour.store': 9,
        other: 9,
        'www.lescomptesdelacrypt.com': 19,
        'tests-non-reg-geoblocked.protectyour.store': 7,
        'tests-non-reg.protectyour.store': 30,
        'api-stg.ogosecurity.com': 4,
        'samuelbenais.fr': 56,
        'drupal.protectyour.store': 4,
        'wdemo.protectyour.store': 12,
      },
    },
    robot: {
      total: 2137,
      dateHistogram: [
        /*
        [1771974000000, 134],
        [1771975800000, 86],
        [1771977600000, 165],
        [1771979400000, 29],
        [1771981200000, 33],
        [1771983000000, 35],
        [1771984800000, 53],
        [1771986600000, 39],
        [1771988400000, 36],
        [1771990200000, 36],
        [1771992000000, 79],
        [1771993800000, 88],
        [1771995600000, 61],
        [1771997400000, 54],
        [1771999200000, 53],
        [1772001000000, 74],
        [1772002800000, 65],
        [1772004600000, 73],
        [1772006400000, 84],
        [1772008200000, 75],
        [1772010000000, 167],
        [1772011800000, 53],
        [1772013600000, 55],
        [1772015400000, 52],
        [1772017200000, 42],
        [1772019000000, 30],
        [1772020800000, 37],
        [1772022600000, 46],
        [1772024400000, 55],
        [1772026200000, 85],
        [1772028000000, 149],
        [1772029800000, 14],
         */
      ],
      bySite: {
        /*
        'stub.ogosecurity.com': 2,
        'lescomptesdelacrypt.com': 1388,
        other: 0,
        'www.lescomptesdelacrypt.com': 735,
        'tests-non-reg.protectyour.store': 11,
        'drupal.protectyour.store': 1,
        */
      },
    },
    banned: {
      total: 144,
      dateHistogram: [
        [1771974000000, 1],
        [1771975800000, 1],
        [1771977600000, 0],
        [1771979400000, 3],
        [1771981200000, 0],
        [1771983000000, 0],
        [1771984800000, 1],
        [1771986600000, 56],
        [1771988400000, 0],
        [1771990200000, 2],
        [1771992000000, 2],
        [1771993800000, 16],
        [1771995600000, 20],
        [1771997400000, 1],
        [1771999200000, 5],
        [1772001000000, 5],
        [1772002800000, 0],
        [1772004600000, 0],
        [1772006400000, 0],
        [1772008200000, 2],
        [1772010000000, 0],
        [1772011800000, 4],
        [1772013600000, 6],
        [1772015400000, 5],
        [1772017200000, 3],
        [1772019000000, 1],
        [1772020800000, 1],
        [1772022600000, 1],
        [1772024400000, 0],
        [1772026200000, 0],
        [1772028000000, 8],
        [1772029800000, 0],
      ],
      bySite: {
        'lescomptesdelacrypt.com': 50,
        'owasp.protectyour.store': 4,
        other: 1,
        'www.lescomptesdelacrypt.com': 5,
        'joomla.protectyour.store': 1,
        'tests-non-reg.protectyour.store': 23,
        'api-stg.ogosecurity.com': 1,
        'samuelbenais.fr': 50,
        'drupal.protectyour.store': 1,
        'wdemo.protectyour.store': 7,
        'gb.ogosecurity.com': 1,
      },
    },
    suspect: {
      total: 68,
      dateHistogram: [
        [1771974000000, 0],
        [1771975800000, 0],
        [1771977600000, 0],
        [1771979400000, 0],
        [1771981200000, 0],
        [1771983000000, 0],
        [1771984800000, 0],
        [1771986600000, 2],
        [1771988400000, 14],
        [1771990200000, 0],
        [1771992000000, 0],
        [1771993800000, 7],
        [1771995600000, 17],
        [1771997400000, 0],
        [1771999200000, 1],
        [1772001000000, 0],
        [1772002800000, 0],
        [1772004600000, 0],
        [1772006400000, 0],
        [1772008200000, 0],
        [1772010000000, 0],
        [1772011800000, 0],
        [1772013600000, 7],
        [1772015400000, 0],
        [1772017200000, 1],
        [1772019000000, 0],
        [1772020800000, 0],
        [1772022600000, 0],
        [1772024400000, 6],
        [1772026200000, 4],
        [1772028000000, 8],
        [1772029800000, 1],
      ],
      bySite: {
        'stub.ogosecurity.com': 6,
        'lescomptesdelacrypt.com': 15,
        'owasp.protectyour.store': 1,
        other: 0,
        'tests-non-reg.protectyour.store': 6,
        'samuelbenais.fr': 24,
        'drupal.protectyour.store': 14,
        'wdemo.protectyour.store': 2,
      },
    },
    analyzed: {
      total: 2754,
      dateHistogram: [
        [1771974000000, 162],
        [1771975800000, 91],
        [1771977600000, 180],
        [1771979400000, 32],
        [1771981200000, 45],
        [1771983000000, 87],
        [1771984800000, 58],
        [1771986600000, 108],
        [1771988400000, 62],
        [1771990200000, 41],
        [1771992000000, 89],
        [1771993800000, 107],
        [1771995600000, 142],
        [1771997400000, 59],
        [1771999200000, 70],
        [1772001000000, 80],
        [1772002800000, 74],
        [1772004600000, 92],
        [1772006400000, 96],
        [1772008200000, 78],
        [1772010000000, 175],
        [1772011800000, 61],
        [1772013600000, 74],
        [1772015400000, 47],
        [1772017200000, 46],
        [1772019000000, 39],
        [1772020800000, 45],
        [1772022600000, 50],
        [1772024400000, 67],
        [1772026200000, 212],
        [1772028000000, 166],
        [1772029800000, 19],
      ],
      bySite: {
        'stub.ogosecurity.com': 24,
        'lescomptesdelacrypt.com': 1575,
        'owasp.protectyour.store': 12,
        other: 24,
        'www.lescomptesdelacrypt.com': 792,
        'tests-non-reg.protectyour.store': 47,
        'api-stg.ogosecurity.com': 21,
        'samuelbenais.fr': 210,
        'drupal.protectyour.store': 25,
        'wdemo.protectyour.store': 17,
        'gb.ogosecurity.com': 7,
      },
    },
    attack: {
      total: 292,
      dateHistogram: [
        [1771974000000, 5],
        [1771975800000, 4],
        [1771977600000, 7],
        [1771979400000, 6],
        [1771981200000, 5],
        [1771983000000, 3],
        [1771984800000, 7],
        [1771986600000, 60],
        [1771988400000, 5],
        [1771990200000, 6],
        [1771992000000, 7],
        [1771993800000, 23],
        [1771995600000, 25],
        [1771997400000, 4],
        [1771999200000, 16],
        [1772001000000, 8],
        [1772002800000, 4],
        [1772004600000, 4],
        [1772006400000, 5],
        [1772008200000, 6],
        [1772010000000, 2],
        [1772011800000, 6],
        [1772013600000, 12],
        [1772015400000, 7],
        [1772017200000, 10],
        [1772019000000, 5],
        [1772020800000, 9],
        [1772022600000, 3],
        [1772024400000, 9],
        [1772026200000, 3],
        [1772028000000, 16],
        [1772029800000, 0],
      ],
      bySite: {
        'stub.ogosecurity.com': 4,
        'lescomptesdelacrypt.com': 138,
        'owasp.protectyour.store': 9,
        other: 9,
        'www.lescomptesdelacrypt.com': 19,
        'tests-non-reg-geoblocked.protectyour.store': 7,
        'tests-non-reg.protectyour.store': 30,
        'api-stg.ogosecurity.com': 4,
        'samuelbenais.fr': 56,
        'drupal.protectyour.store': 4,
        'wdemo.protectyour.store': 12,
      },
    },
  },
  responsesCodes: {
    '200': {
      data: [
        [1791178200000, 3350],
        [1791212400000, 4250],
        [1791199800000, 5181],
        [1791162000000, 3188],
        [1791198000000, 4605],
        [1791156600000, 3186],
        [1791192600000, 5983],
        [1791190800000, 6053],
        [1791176400000, 3440],
        [1791181800000, 4036],
        [1791167400000, 3524],
        [1791154800000, 3308],
        [1791203400000, 7591],
        [1791180000000, 3889],
        [1791201600000, 6109],
        [1791165600000, 3096],
        [1791205200000, 6849],
        [1791207000000, 7283],
        [1791160200000, 2979],
        [1791171000000, 3116],
        [1791183600000, 6417],
        [1791185400000, 8480],
        [1791169200000, 3292],
        [1791210600000, 6864],
        [1791153000000, 3205],
        [1791208800000, 7028],
        [1791151200000, 4299],
        [1791163800000, 2797],
        [1791174600000, 3465],
        [1791187200000, 6274],
        [1791196200000, 4161],
        [1791189000000, 6825],
        [1791158400000, 3263],
        [1791194400000, 10303],
        [1791172800000, 3298],
      ],
    },
    '201': {
      data: [
        [1791178200000, 6460],
        [1791212400000, 5346],
        [1791199800000, 5963],
        [1791162000000, 6032],
        [1791198000000, 12760],
        [1791156600000, 5949],
        [1791192600000, 6163],
        [1791190800000, 11589],
        [1791176400000, 6094],
        [1791181800000, 5934],
        [1791167400000, 8302],
        [1791154800000, 6810],
        [1791203400000, 6005],
        [1791180000000, 12644],
        [1791201600000, 6017],
        [1791165600000, 6016],
        [1791205200000, 8897],
        [1791207000000, 9709],
        [1791160200000, 5219],
        [1791171000000, 5954],
        [1791183600000, 6369],
        [1791185400000, 8318],
        [1791169200000, 8589],
        [1791210600000, 6418],
        [1791153000000, 5738],
        [1791208800000, 13254],
        [1791151200000, 6682],
        [1791163800000, 5451],
        [1791174600000, 6042],
        [1791187200000, 7256],
        [1791196200000, 5823],
        [1791189000000, 8034],
        [1791158400000, 5849],
        [1791194400000, 6519],
        [1791172800000, 6625],
      ],
    },
    '499': {
      data: [
        [1791178200000, 2],
        [1791212400000, 9],
        [1791199800000, 11],
        [1791162000000, 0],
        [1791198000000, 11],
        [1791156600000, 0],
        [1791192600000, 15],
        [1791190800000, 12],
        [1791176400000, 0],
        [1791181800000, 3],
        [1791167400000, 60],
        [1791154800000, 0],
        [1791203400000, 27],
        [1791180000000, 1],
        [1791201600000, 23],
        [1791165600000, 82],
        [1791205200000, 19],
        [1791207000000, 15],
        [1791160200000, 0],
        [1791171000000, 0],
        [1791183600000, 23],
        [1791185400000, 43],
        [1791169200000, 2],
        [1791210600000, 37],
        [1791153000000, 0],
        [1791208800000, 22],
        [1791151200000, 0],
        [1791163800000, 15],
        [1791174600000, 0],
        [1791187200000, 57],
        [1791196200000, 14],
        [1791189000000, 103],
        [1791158400000, 1],
        [1791194400000, 85],
        [1791172800000, 0],
      ],
    },
    '301': {
      data: [
        [1791178200000, 6],
        [1791212400000, 5],
        [1791199800000, 6],
        [1791162000000, 7],
        [1791198000000, 19],
        [1791156600000, 0],
        [1791192600000, 9],
        [1791190800000, 5],
        [1791176400000, 7],
        [1791181800000, 3],
        [1791167400000, 4],
        [1791154800000, 2],
        [1791203400000, 1],
        [1791180000000, 5],
        [1791201600000, 2],
        [1791165600000, 3],
        [1791205200000, 7],
        [1791207000000, 10],
        [1791160200000, 6],
        [1791171000000, 7],
        [1791183600000, 7],
        [1791185400000, 13],
        [1791169200000, 3],
        [1791210600000, 16],
        [1791153000000, 0],
        [1791208800000, 6],
        [1791151200000, 16],
        [1791163800000, 5],
        [1791174600000, 6],
        [1791187200000, 6],
        [1791196200000, 3],
        [1791189000000, 7],
        [1791158400000, 0],
        [1791194400000, 3],
        [1791172800000, 0],
      ],
    },
    '400': {
      data: [
        [1791178200000, 1],
        [1791212400000, 0],
        [1791199800000, 1],
        [1791162000000, 1],
        [1791198000000, 0],
        [1791156600000, 0],
        [1791192600000, 11],
        [1791190800000, 3],
        [1791176400000, 2],
        [1791181800000, 1],
        [1791167400000, 1],
        [1791154800000, 0],
        [1791203400000, 7],
        [1791180000000, 0],
        [1791201600000, 2],
        [1791165600000, 1],
        [1791205200000, 6],
        [1791207000000, 29],
        [1791160200000, 1],
        [1791171000000, 6],
        [1791183600000, 2],
        [1791185400000, 3],
        [1791169200000, 3],
        [1791210600000, 4],
        [1791153000000, 0],
        [1791208800000, 2],
        [1791151200000, 1],
        [1791163800000, 3],
        [1791174600000, 0],
        [1791187200000, 6],
        [1791196200000, 5],
        [1791189000000, 1],
        [1791158400000, 6],
        [1791194400000, 7],
        [1791172800000, 2],
      ],
    },
    '302': {
      data: [
        [1791178200000, 107],
        [1791212400000, 66],
        [1791199800000, 142],
        [1791162000000, 148],
        [1791198000000, 73],
        [1791156600000, 127],
        [1791192600000, 95],
        [1791190800000, 130],
        [1791176400000, 158],
        [1791181800000, 72],
        [1791167400000, 84],
        [1791154800000, 87],
        [1791203400000, 176],
        [1791180000000, 121],
        [1791201600000, 99],
        [1791165600000, 113],
        [1791205200000, 83],
        [1791207000000, 275],
        [1791160200000, 97],
        [1791171000000, 111],
        [1791183600000, 127],
        [1791185400000, 153],
        [1791169200000, 107],
        [1791210600000, 124],
        [1791153000000, 98],
        [1791208800000, 113],
        [1791151200000, 106],
        [1791163800000, 77],
        [1791174600000, 109],
        [1791187200000, 118],
        [1791196200000, 106],
        [1791189000000, 107],
        [1791158400000, 145],
        [1791194400000, 210],
        [1791172800000, 89],
      ],
    },
    '401': {
      data: [
        [1791178200000, 0],
        [1791212400000, 11],
        [1791199800000, 20],
        [1791162000000, 1],
        [1791198000000, 27],
        [1791156600000, 4],
        [1791192600000, 21],
        [1791190800000, 33],
        [1791176400000, 2],
        [1791181800000, 7],
        [1791167400000, 0],
        [1791154800000, 37],
        [1791203400000, 67],
        [1791180000000, 3],
        [1791201600000, 44],
        [1791165600000, 0],
        [1791205200000, 20],
        [1791207000000, 25],
        [1791160200000, 30],
        [1791171000000, 0],
        [1791183600000, 68],
        [1791185400000, 74],
        [1791169200000, 0],
        [1791210600000, 22],
        [1791153000000, 27],
        [1791208800000, 27],
        [1791151200000, 0],
        [1791163800000, 0],
        [1791174600000, 0],
        [1791187200000, 40],
        [1791196200000, 14],
        [1791189000000, 97],
        [1791158400000, 0],
        [1791194400000, 47],
        [1791172800000, 1],
      ],
    },
    '500': {
      data: [
        [1791178200000, 0],
        [1791212400000, 1],
        [1791199800000, 1],
        [1791162000000, 0],
        [1791198000000, 3],
        [1791156600000, 0],
        [1791192600000, 1],
        [1791190800000, 2],
        [1791176400000, 0],
        [1791181800000, 1],
        [1791167400000, 0],
        [1791154800000, 2],
        [1791203400000, 2],
        [1791180000000, 0],
        [1791201600000, 3],
        [1791165600000, 0],
        [1791205200000, 1],
        [1791207000000, 2],
        [1791160200000, 0],
        [1791171000000, 0],
        [1791183600000, 11],
        [1791185400000, 6],
        [1791169200000, 0],
        [1791210600000, 2],
        [1791153000000, 0],
        [1791208800000, 3],
        [1791151200000, 0],
        [1791163800000, 0],
        [1791174600000, 0],
        [1791187200000, 3],
        [1791196200000, 1],
        [1791189000000, 3],
        [1791158400000, 0],
        [1791194400000, 3],
        [1791172800000, 0],
      ],
    },
    '204': {
      data: [
        [1791178200000, 0],
        [1791212400000, 0],
        [1791199800000, 0],
        [1791162000000, 0],
        [1791198000000, 0],
        [1791156600000, 0],
        [1791192600000, 0],
        [1791190800000, 0],
        [1791176400000, 0],
        [1791181800000, 0],
        [1791167400000, 0],
        [1791154800000, 0],
        [1791203400000, 21],
        [1791180000000, 0],
        [1791201600000, 1],
        [1791165600000, 0],
        [1791205200000, 1],
        [1791207000000, 3],
        [1791160200000, 0],
        [1791171000000, 0],
        [1791183600000, 3],
        [1791185400000, 2],
        [1791169200000, 0],
        [1791210600000, 7],
        [1791153000000, 0],
        [1791208800000, 1],
        [1791151200000, 0],
        [1791163800000, 0],
        [1791174600000, 0],
        [1791187200000, 17],
        [1791196200000, 1],
        [1791189000000, 0],
        [1791158400000, 0],
        [1791194400000, 2],
        [1791172800000, 0],
      ],
    },
    '403': {
      data: [
        [1791178200000, 514],
        [1791212400000, 676],
        [1791199800000, 7],
        [1791162000000, 1475],
        [1791198000000, 445],
        [1791156600000, 920],
        [1791192600000, 123],
        [1791190800000, 284],
        [1791176400000, 500],
        [1791181800000, 9],
        [1791167400000, 2611],
        [1791154800000, 284],
        [1791203400000, 477],
        [1791180000000, 11],
        [1791201600000, 42],
        [1791165600000, 2],
        [1791205200000, 666],
        [1791207000000, 605],
        [1791160200000, 25],
        [1791171000000, 704],
        [1791183600000, 729],
        [1791185400000, 549],
        [1791169200000, 21],
        [1791210600000, 937],
        [1791153000000, 38],
        [1791208800000, 417],
        [1791151200000, 14],
        [1791163800000, 134],
        [1791174600000, 271],
        [1791187200000, 348],
        [1791196200000, 402],
        [1791189000000, 561],
        [1791158400000, 109],
        [1791194400000, 252],
        [1791172800000, 6],
      ],
    },
    '304': {
      data: [
        [1791178200000, 63],
        [1791212400000, 46],
        [1791199800000, 98],
        [1791162000000, 1],
        [1791198000000, 126],
        [1791156600000, 28],
        [1791192600000, 378],
        [1791190800000, 87],
        [1791176400000, 44],
        [1791181800000, 112],
        [1791167400000, 3],
        [1791154800000, 2],
        [1791203400000, 372],
        [1791180000000, 108],
        [1791201600000, 336],
        [1791165600000, 3],
        [1791205200000, 176],
        [1791207000000, 225],
        [1791160200000, 64],
        [1791171000000, 25],
        [1791183600000, 669],
        [1791185400000, 409],
        [1791169200000, 1],
        [1791210600000, 175],
        [1791153000000, 54],
        [1791208800000, 96],
        [1791151200000, 16],
        [1791163800000, 5],
        [1791174600000, 63],
        [1791187200000, 231],
        [1791196200000, 99],
        [1791189000000, 383],
        [1791158400000, 4],
        [1791194400000, 42],
        [1791172800000, 4],
      ],
    },
    '404': {
      data: [
        [1791178200000, 20],
        [1791212400000, 14],
        [1791199800000, 35],
        [1791162000000, 24],
        [1791198000000, 19],
        [1791156600000, 48],
        [1791192600000, 46],
        [1791190800000, 89],
        [1791176400000, 70],
        [1791181800000, 52],
        [1791167400000, 9],
        [1791154800000, 76],
        [1791203400000, 49],
        [1791180000000, 6],
        [1791201600000, 25],
        [1791165600000, 14],
        [1791205200000, 61],
        [1791207000000, 83],
        [1791160200000, 6],
        [1791171000000, 6],
        [1791183600000, 17],
        [1791185400000, 62],
        [1791169200000, 8],
        [1791210600000, 58],
        [1791153000000, 13],
        [1791208800000, 56],
        [1791151200000, 6],
        [1791163800000, 4],
        [1791174600000, 30],
        [1791187200000, 28],
        [1791196200000, 6],
        [1791189000000, 41],
        [1791158400000, 38],
        [1791194400000, 19],
        [1791172800000, 2],
      ],
    },
    '206': {
      data: [
        [1791178200000, 0],
        [1791212400000, 0],
        [1791199800000, 0],
        [1791162000000, 0],
        [1791198000000, 5],
        [1791156600000, 0],
        [1791192600000, 2],
        [1791190800000, 4],
        [1791176400000, 0],
        [1791181800000, 0],
        [1791167400000, 0],
        [1791154800000, 0],
        [1791203400000, 0],
        [1791180000000, 8],
        [1791201600000, 2],
        [1791165600000, 0],
        [1791205200000, 0],
        [1791207000000, 0],
        [1791160200000, 6],
        [1791171000000, 0],
        [1791183600000, 1],
        [1791185400000, 2],
        [1791169200000, 0],
        [1791210600000, 0],
        [1791153000000, 11],
        [1791208800000, 0],
        [1791151200000, 3],
        [1791163800000, 3],
        [1791174600000, 6],
        [1791187200000, 0],
        [1791196200000, 0],
        [1791189000000, 0],
        [1791158400000, 2],
        [1791194400000, 0],
        [1791172800000, 0],
      ],
    },
    '207': {
      data: [
        [1791178200000, 30],
        [1791212400000, 25],
        [1791199800000, 29],
        [1791162000000, 24],
        [1791198000000, 48],
        [1791156600000, 14],
        [1791192600000, 33],
        [1791190800000, 40],
        [1791176400000, 36],
        [1791181800000, 23],
        [1791167400000, 25],
        [1791154800000, 23],
        [1791203400000, 36],
        [1791180000000, 22],
        [1791201600000, 37],
        [1791165600000, 18],
        [1791205200000, 23],
        [1791207000000, 33],
        [1791160200000, 15],
        [1791171000000, 19],
        [1791183600000, 77],
        [1791185400000, 63],
        [1791169200000, 25],
        [1791210600000, 39],
        [1791153000000, 26],
        [1791208800000, 41],
        [1791151200000, 22],
        [1791163800000, 23],
        [1791174600000, 24],
        [1791187200000, 72],
        [1791196200000, 28],
        [1791189000000, 46],
        [1791158400000, 18],
        [1791194400000, 44],
        [1791172800000, 40],
      ],
    },
    '429': {
      data: [
        [1791178200000, 0],
        [1791212400000, 0],
        [1791199800000, 25],
        [1791162000000, 0],
        [1791198000000, 30],
        [1791156600000, 0],
        [1791192600000, 27],
        [1791190800000, 30],
        [1791176400000, 0],
        [1791181800000, 0],
        [1791167400000, 0],
        [1791154800000, 0],
        [1791203400000, 22],
        [1791180000000, 0],
        [1791201600000, 22],
        [1791165600000, 0],
        [1791205200000, 22],
        [1791207000000, 22],
        [1791160200000, 0],
        [1791171000000, 0],
        [1791183600000, 17],
        [1791185400000, 27],
        [1791169200000, 0],
        [1791210600000, 7],
        [1791153000000, 0],
        [1791208800000, 12],
        [1791151200000, 0],
        [1791163800000, 0],
        [1791174600000, 0],
        [1791187200000, 20],
        [1791196200000, 17],
        [1791189000000, 27],
        [1791158400000, 0],
        [1791194400000, 30],
        [1791172800000, 0],
      ],
    },
  },
  responseTime: {
    traffic: {
      data: [
        [1791178200000, 10553],
        [1791212400000, 10454],
        [1791199800000, 11521],
        [1791162000000, 10901],
        [1791198000000, 18172],
        [1791156600000, 10278],
        [1791192600000, 12907],
        [1791190800000, 18361],
        [1791176400000, 10354],
        [1791181800000, 10254],
        [1791167400000, 14623],
        [1791154800000, 10637],
        [1791203400000, 14872],
        [1791180000000, 16818],
        [1791201600000, 12764],
        [1791165600000, 9381],
        [1791205200000, 16840],
        [1791207000000, 18335],
        [1791160200000, 8448],
        [1791171000000, 9948],
        [1791183600000, 14537],
        [1791185400000, 18205],
        [1791169200000, 12051],
        [1791210600000, 14712],
        [1791153000000, 9210],
        [1791208800000, 21079],
        [1791151200000, 11165],
        [1791163800000, 8517],
        [1791174600000, 10016],
        [1791187200000, 14476],
        [1791196200000, 10685],
        [1791189000000, 16236],
        [1791158400000, 9435],
        [1791194400000, 17566],
        [1791172800000, 10070],
      ],
    },
    responseTime: {
      '50': {
        data: [
          [1791178200000, 97.59833603423768],
          [1791212400000, 88.34691981819962],
          [1791199800000, 90.05147624849104],
          [1791162000000, 77.8864039349728],
          [1791198000000, 112.52074734904095],
          [1791156600000, 74.39304614699121],
          [1791192600000, 83.26816893218975],
          [1791190800000, 149.17601966925076],
          [1791176400000, 88.109282265164],
          [1791181800000, 97.0994516478099],
          [1791167400000, 65.49371588377097],
          [1791154800000, 104.16846798619828],
          [1791203400000, 77.6871854683631],
          [1791180000000, 119.2843296259705],
          [1791201600000, 104.85192687813684],
          [1791165600000, 101.2034291691812],
          [1791205200000, 95.14184343962194],
          [1791207000000, 101.42841235306894],
          [1791160200000, 100.3455347718782],
          [1791171000000, 94.19507682664437],
          [1791183600000, 68.63856382245255],
          [1791185400000, 94.2181235365838],
          [1791169200000, 102.62300361660138],
          [1791210600000, 79.95242073610547],
          [1791153000000, 102.70077412263181],
          [1791208800000, 128.49014611963332],
          [1791151200000, 86.11016316874334],
          [1791163800000, 103.41967227543881],
          [1791174600000, 90.1250961755145],
          [1791187200000, 118.4697653553988],
          [1791196200000, 84.76896022328432],
          [1791189000000, 99.08589420216435],
          [1791158400000, 97.68875438846685],
          [1791194400000, 65.1293603852315],
          [1791172800000, 107.38682302290044],
        ],
      },
      '75': {
        data: [
          [1791178200000, 268.0446562103302],
          [1791212400000, 267.1550100858018],
          [1791199800000, 268.1057786232198],
          [1791162000000, 248.74598054572357],
          [1791198000000, 236.15515431150084],
          [1791156600000, 241.781283945179],
          [1791192600000, 273.54923914087044],
          [1791190800000, 289.1761723407478],
          [1791176400000, 268.63871113288593],
          [1791181800000, 269.9440413071079],
          [1791167400000, 203.44667129714082],
          [1791154800000, 255.30216834258545],
          [1791203400000, 267.7493181699078],
          [1791180000000, 244.00078596937934],
          [1791201600000, 290.51325746154777],
          [1791165600000, 267.784595233324],
          [1791205200000, 254.0159553769656],
          [1791207000000, 254.40206903731325],
          [1791160200000, 254.93027434695503],
          [1791171000000, 271.41699633675626],
          [1791183600000, 255.00789794566984],
          [1791185400000, 290.29327745135487],
          [1791169200000, 244.01679920499436],
          [1791210600000, 272.7920754993747],
          [1791153000000, 259.3197898643856],
          [1791208800000, 253.27789732922054],
          [1791151200000, 251.22303982181342],
          [1791163800000, 262.0256649621323],
          [1791174600000, 273.9527430452473],
          [1791187200000, 291.3632301865141],
          [1791196200000, 263.56285139192676],
          [1791189000000, 273.81595883299934],
          [1791158400000, 261.0534417995033],
          [1791194400000, 235.9292177533656],
          [1791172800000, 272.25792761134795],
        ],
      },
      '95': {
        data: [
          [1791178200000, 417.2300261572058],
          [1791212400000, 536.0259957170664],
          [1791199800000, 497.3645616602804],
          [1791162000000, 388.23741016188666],
          [1791198000000, 349.8484276608362],
          [1791156600000, 376.74570468341614],
          [1791192600000, 598.6540370818684],
          [1791190800000, 556.0890192909077],
          [1791176400000, 415.952339478013],
          [1791181800000, 494.34424699415223],
          [1791167400000, 336.49364654527034],
          [1791154800000, 416.9934979835205],
          [1791203400000, 652.5132860548022],
          [1791180000000, 349.63645962016625],
          [1791201600000, 584.6275895845931],
          [1791165600000, 436.05781846105845],
          [1791205200000, 661.9291159271265],
          [1791207000000, 655.2431106851959],
          [1791160200000, 396.42619955301393],
          [1791171000000, 433.90503137920155],
          [1791183600000, 600.1825810253888],
          [1791185400000, 618.851253947287],
          [1791169200000, 377.06044615096687],
          [1791210600000, 750.8646081395209],
          [1791153000000, 442.4239874037769],
          [1791208800000, 462.5197866702191],
          [1791151200000, 391.02913889844746],
          [1791163800000, 409.1260430474783],
          [1791174600000, 474.2684003545969],
          [1791187200000, 701.2457778502524],
          [1791196200000, 420.2128116680447],
          [1791189000000, 661.1913337070177],
          [1791158400000, 414.66475192709544],
          [1791194400000, 547.909551591892],
          [1791172800000, 409.0763226278476],
        ],
      },
    },
  },
  bandwidth: {
    bandwidth: {
      data: [
        [1791178200000, 44059022],
        [1791212400000, 229098953],
        [1791199800000, 1722910069],
        [1791162000000, 162900642],
        [1791198000000, 166214050],
        [1791156600000, 40835333],
        [1791192600000, 117381644],
        [1791190800000, 277786969],
        [1791176400000, 214319459],
        [1791181800000, 65366080],
        [1791167400000, 54782681],
        [1791154800000, 154555365],
        [1791203400000, 243837181],
        [1791180000000, 289482813],
        [1791201600000, 229692853],
        [1791165600000, 144035582],
        [1791205200000, 223834455],
        [1791207000000, 175255482],
        [1791160200000, 50304215],
        [1791171000000, 39414403],
        [1791183600000, 310772979],
        [1791185400000, 211840960],
        [1791169200000, 137980824],
        [1791210600000, 187600387],
        [1791153000000, 49649532],
        [1791208800000, 196251589],
        [1791151200000, 303863762],
        [1791163800000, 25928402],
        [1791174600000, 55900774],
        [1791187200000, 267755093],
        [1791196200000, 172938485],
        [1791189000000, 1313257681],
        [1791158400000, 153906946],
        [1791194400000, 469804607],
        [1791172800000, 1373308429],
      ],
    },
    contentLength: {
      '50': {
        data: [
          [1791178200000, 16.0],
          [1791212400000, 16.0],
          [1791199800000, 16.0],
          [1791162000000, 16.0],
          [1791198000000, 16.0],
          [1791156600000, 16.0],
          [1791192600000, 16.0],
          [1791190800000, 16.0],
          [1791176400000, 16.0],
          [1791181800000, 16.0],
          [1791167400000, 16.0],
          [1791154800000, 16.0],
          [1791203400000, 16.43415372199527],
          [1791180000000, 16.0],
          [1791201600000, 16.0],
          [1791165600000, 16.0],
          [1791205200000, 16.0],
          [1791207000000, 16.0],
          [1791160200000, 16.0],
          [1791171000000, 16.0],
          [1791183600000, 16.0],
          [1791185400000, 16.0],
          [1791169200000, 16.0],
          [1791210600000, 16.0],
          [1791153000000, 16.0],
          [1791208800000, 16.0],
          [1791151200000, 16.0],
          [1791163800000, 16.0],
          [1791174600000, 16.0],
          [1791187200000, 16.0],
          [1791196200000, 16.0],
          [1791189000000, 16.0],
          [1791158400000, 16.0],
          [1791194400000, 53.48720092526035],
          [1791172800000, 16.0],
        ],
      },
      '75': {
        data: [
          [1791178200000, 16.0],
          [1791212400000, 80.91818609526798],
          [1791199800000, 225.89329497745354],
          [1791162000000, 16.0],
          [1791198000000, 16.0],
          [1791156600000, 16.0],
          [1791192600000, 450.12230491638735],
          [1791190800000, 38.183456571937654],
          [1791176400000, 16.0],
          [1791181800000, 19.2041137226085],
          [1791167400000, 16.0],
          [1791154800000, 16.0],
          [1791203400000, 703.2223695433205],
          [1791180000000, 16.0],
          [1791201600000, 338.39763034052714],
          [1791165600000, 16.0],
          [1791205200000, 241.42084321088086],
          [1791207000000, 266.63701019830313],
          [1791160200000, 16.0],
          [1791171000000, 16.0],
          [1791183600000, 469.46608393940824],
          [1791185400000, 849.4673813113659],
          [1791169200000, 16.0],
          [1791210600000, 453.80462130482266],
          [1791153000000, 16.0],
          [1791208800000, 39.942678721588415],
          [1791151200000, 50.167950558809274],
          [1791163800000, 16.0],
          [1791174600000, 16.0],
          [1791187200000, 240.49049844236805],
          [1791196200000, 57.946142499822116],
          [1791189000000, 205.03695720153343],
          [1791158400000, 16.0],
          [1791194400000, 2812.019833167811],
          [1791172800000, 16.0],
        ],
      },
      '95': {
        data: [
          [1791178200000, 12849.556650313172],
          [1791212400000, 23322.907298989412],
          [1791199800000, 21259.17139257634],
          [1791162000000, 15721.267594438465],
          [1791198000000, 6209.4198189030485],
          [1791156600000, 8333.308774229594],
          [1791192600000, 24187.246191242768],
          [1791190800000, 16071.771740787495],
          [1791176400000, 50102.865652892244],
          [1791181800000, 18253.93020014418],
          [1791167400000, 2187.0919180239166],
          [1791154800000, 23960.844777885854],
          [1791203400000, 51602.92557940654],
          [1791180000000, 6361.276172071026],
          [1791201600000, 20567.15853148959],
          [1791165600000, 14953.00765504711],
          [1791205200000, 21645.42619081106],
          [1791207000000, 28581.07095392847],
          [1791160200000, 10943.919174411989],
          [1791171000000, 13090.125048181957],
          [1791183600000, 51503.21293140144],
          [1791185400000, 36336.22356229911],
          [1791169200000, 9357.496623330308],
          [1791210600000, 32599.68879322649],
          [1791153000000, 18493.838498789344],
          [1791208800000, 13232.426509652494],
          [1791151200000, 28731.87950329989],
          [1791163800000, 6845.747045240479],
          [1791174600000, 21312.19823934482],
          [1791187200000, 24068.763358005013],
          [1791196200000, 16178.519747381493],
          [1791189000000, 26047.219155234965],
          [1791158400000, 21775.82811459],
          [1791194400000, 50696.59705396828],
          [1791172800000, 45545.68884096574],
        ],
      },
    },
    cacheStatus: {
      HIT_CDN: {
        data: [
          [1791178200000, 7945336],
          [1791212400000, 24739195],
          [1791199800000, 12113018],
          [1791162000000, 26169410],
          [1791198000000, 16886247],
          [1791156600000, 20864820],
          [1791192600000, 30710439],
          [1791190800000, 24198522],
          [1791176400000, 25233546],
          [1791181800000, 9729173],
          [1791167400000, 7411473],
          [1791154800000, 7133964],
          [1791203400000, 106313582],
          [1791180000000, 8262072],
          [1791201600000, 20628863],
          [1791165600000, 16350131],
          [1791205200000, 53504474],
          [1791207000000, 97238433],
          [1791160200000, 8818156],
          [1791171000000, 13237541],
          [1791183600000, 23675261],
          [1791185400000, 95020857],
          [1791169200000, 8878789],
          [1791210600000, 47274489],
          [1791153000000, 12564494],
          [1791208800000, 15128518],
          [1791151200000, 77997527],
          [1791163800000, 6252235],
          [1791174600000, 10558054],
          [1791187200000, 10102828],
          [1791196200000, 41636824],
          [1791189000000, 48330829],
          [1791158400000, 15754028],
          [1791194400000, 47105511],
          [1791172800000, 7003114],
        ],
      },
      HIT: {
        data: [
          [1791178200000, 0],
          [1791212400000, 0],
          [1791199800000, 0],
          [1791162000000, 0],
          [1791198000000, 0],
          [1791156600000, 0],
          [1791192600000, 0],
          [1791190800000, 0],
          [1791176400000, 0],
          [1791181800000, 0],
          [1791167400000, 0],
          [1791154800000, 0],
          [1791203400000, 0],
          [1791180000000, 0],
          [1791201600000, 0],
          [1791165600000, 0],
          [1791205200000, 0],
          [1791207000000, 0],
          [1791160200000, 0],
          [1791171000000, 0],
          [1791183600000, 0],
          [1791185400000, 0],
          [1791169200000, 0],
          [1791210600000, 0],
          [1791153000000, 0],
          [1791208800000, 0],
          [1791151200000, 0],
          [1791163800000, 0],
          [1791174600000, 0],
          [1791187200000, 0],
          [1791196200000, 0],
          [1791189000000, 0],
          [1791158400000, 0],
          [1791194400000, 0],
          [1791172800000, 0],
        ],
      },
      NONE: {
        data: [
          [1791178200000, 33382531],
          [1791212400000, 202291800],
          [1791199800000, 41419718],
          [1791162000000, 131993853],
          [1791198000000, 139652854],
          [1791156600000, 19408280],
          [1791192600000, 74084159],
          [1791190800000, 235645440],
          [1791176400000, 188408055],
          [1791181800000, 35102563],
          [1791167400000, 45541441],
          [1791154800000, 146376223],
          [1791203400000, 129255324],
          [1791180000000, 280007538],
          [1791201600000, 202023887],
          [1791165600000, 124213550],
          [1791205200000, 165869420],
          [1791207000000, 60679175],
          [1791160200000, 20714193],
          [1791171000000, 23031432],
          [1791183600000, 278729873],
          [1791185400000, 107000807],
          [1791169200000, 126769567],
          [1791210600000, 125288946],
          [1791153000000, 35315332],
          [1791208800000, 172608456],
          [1791151200000, 219779866],
          [1791163800000, 16759234],
          [1791174600000, 43029449],
          [1791187200000, 252191902],
          [1791196200000, 125420149],
          [1791189000000, 1249436054],
          [1791158400000, 133462786],
          [1791194400000, 420277788],
          [1791172800000, 1352799910],
        ],
      },
      MISS: {
        data: [
          [1791178200000, 2731155],
          [1791212400000, 2067958],
          [1791199800000, 1669377333],
          [1791162000000, 4375395],
          [1791198000000, 9566992],
          [1791156600000, 562233],
          [1791192600000, 12587046],
          [1791190800000, 17943007],
          [1791176400000, 560196],
          [1791181800000, 20534344],
          [1791167400000, 1829767],
          [1791154800000, 986364],
          [1791203400000, 8209495],
          [1791180000000, 1213203],
          [1791201600000, 7040103],
          [1791165600000, 3471901],
          [1791205200000, 4401789],
          [1791207000000, 17337874],
          [1791160200000, 20771866],
          [1791171000000, 3145430],
          [1791183600000, 8309050],
          [1791185400000, 9819056],
          [1791169200000, 2332468],
          [1791210600000, 14759154],
          [1791153000000, 1769706],
          [1791208800000, 7521595],
          [1791151200000, 6086369],
          [1791163800000, 2916933],
          [1791174600000, 2313271],
          [1791187200000, 5460363],
          [1791196200000, 5881512],
          [1791189000000, 15490798],
          [1791158400000, 4690132],
          [1791194400000, 2362522],
          [1791172800000, 13505405],
        ],
      },
    },
  },
};
// END-NOSCAN

const mockDataTopBlockedIps: BlockedIpData[] = [
  {
    ip: '34.2.29.144',
    countryCode: 'SG',
    blocked: 0,
    siteStats: [
      {
        site: 'toto.com',
        total: 277,
        blockedByBrain: 276,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 0,
      },
    ],
  },
  {
    ip: '35.209.151.76',
    countryCode: 'US',
    blocked: 0,
    siteStats: [
      {
        site: 'toto.com',
        total: 277,
        blockedByBrain: 276,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 0,
      },
    ],
  },
  {
    ip: '35.215.169.213',
    countryCode: 'HK',
    blocked: 0,
    siteStats: [
      {
        site: 'example.com',
        total: 277,
        blockedByBrain: 275,
        blockedByRule: 1,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 0,
      },
      {
        site: 'www.example.com',
        total: 177,
        blockedByBrain: 175,
        blockedByRule: 1,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 0,
      },
    ],
  },
  {
    ip: '35.215.187.108',
    countryCode: 'HK',
    blocked: 0,
    siteStats: [
      {
        site: 'www.ogosecurity.com',
        total: 277,
        blockedByBrain: 276,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 0,
      },
    ],
  },
  {
    ip: '13.70.110.189',
    countryCode: 'AU',
    blocked: 0,
    siteStats: [
      {
        site: 'www.ogosecurity.com',
        total: 282,
        blockedByBrain: 156,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 7,
      },
      {
        site: 'ogosecurity.com',
        total: 59,
        blockedByBrain: 21,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 4,
      },
    ],
  },
  {
    ip: '34.78.64.142',
    countryCode: 'BE',
    blocked: 0,
    siteStats: [
      {
        site: 'ogosecurity.com',
        total: 171,
        blockedByBrain: 116,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 0,
      },
      {
        site: 'www.ogosecurity.com',
        total: 64,
        blockedByBrain: 59,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 5,
      },
    ],
  },
  {
    ip: '34.140.132.132',
    countryCode: 'BE',
    blocked: 0,
    siteStats: [
      {
        site: 'www.ogosecurity.com',
        total: 122,
        blockedByBrain: 113,
        blockedByRule: 1,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 3,
      },
    ],
  },
  {
    ip: '45.156.87.117',
    countryCode: 'NL',
    blocked: 0,
    siteStats: [
      {
        site: 'ogosecurity.com',
        total: 80,
        blockedByBrain: 75,
        blockedByRule: 0,
        blockedByCdn: 0,
        geoblocked: 0,
        challengeEmitted: 0,
        suspicious: 0,
      },
    ],
  },
];
